import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import { Calendar, Clock, ArrowLeft, ArrowRight, Tag, ChevronRight, Share2, Link2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

const blogPosts = [
  {
    id: 'how-merchants-saved-agentic-commerce-adoption',
    title: 'How Merchants Saved Agentic Commerce Adoption',
    excerpt: 'A twelve-page position paper defending three claims: (1) the primitive gap in agentic commerce is measured, twice, a major card network 38-point trust-with-guarantor gap, a Big Four accounting firm 17-point delegation-willingness gap, Fortune 93-point aggregate autonomous-agent trust gap; (2) the merchant edge, not the scheme, not the bank, not the consumer, is the only surface on the four-link agentic supply chain that can refuse a transaction before the payment rail is consulted; (3) the infrastructure is live on Base mainnet, bound to native native USDC, source-verified on BaseScan, and available to any merchant with an authorization callback and a ten-line HTTP verify call. Seventeen independent institutional voices publicly co-signed the primitive gap in September 2026 (including two of the largest US card schemes, a global wallet, a major-marketplace-vs-major-agent-runtime standoff, a scheme EVP\u2019s NYC roundtable readout, a six-bank industry-consortium common-principles publication, and) in the two most recent additions: an agentic-AI solution architect quantifying the delegation gap, and a chief communications officer in banking and critical financial infrastructure publishing the "Detection protects the balance. Response protects the relationship." reframe.',
    date: 'September 27, 2026',
    readTime: '15 min read',
    category: 'Position Paper',
    gradient: 'from-emerald-500 to-teal-500',
    featured: true,
    pdfUrl: '/downloads/kojiru-merchants-saved-agentic-commerce.pdf?v=1',
    body: `**A Kojiru Position Paper \u00b7 September 27, 2026**
*Kojiru, LLC (Nashville, Tennessee) wayne@kojiru.com*

## The one-sentence thesis

> **When an agent harms a consumer, the merchant holds a signed mandate, a public operator pledge, and on-chain evidence to resolve it through their existing dispute policy. Kojiru is infrastructure, not insurance.**

Three actors, three roles. The **merchant** owns the dispute policy. The **operator\u2019s pledged USDC** is a public commitment signal. **Kojiru** is infrastructure, not a party to the transaction, not a custodian of funds, not an adjudicator of claims, not an insurer of outcomes. Kojiru operates the evidence primitive.

## The gap is measured: twice, independently

Three institutional research publications converge on the same shape of a gap:

- **A major card network (September 2026):** Consumer trust in an agent-initiated transaction rises from 23% to 61% when a recognized guarantor stands behind the agent, a 38-point trust-with-a-guarantor gap.
- **A Big Four accounting firm (2026):** 46% of millennials expect AI to complete purchases; only 29% of consumers are comfortable letting it pay, a 17-point delegation-willingness gap.
- **Fortune (September 2026):** Consumer trust in autonomous agent-initiated commerce sits 93 points below trust in a human-completed transaction.

Three surveys. Three publishers. Three quantitatively precise gaps. The variable that closes the gap in every model where the closing variable is named is the same: a party that pledges recourse before the agent is authorized to transact.

## The detection-vs-response reframe

A September 26, 2026 public LinkedIn post by a chief communications officer in banking, payments, and critical financial infrastructure named the reframe that carries this paper:

> **"Detection protects the balance. Response protects the relationship."**

The four-row stack the agentic commerce industry has shipped (**identity**, **intent**, **attestation**, **settlement**) is entirely a detection stack. None of it is a mechanism for what happens next when the transaction was authorized on-scope but produced a wrong outcome.

The same operator captured the failure mode in the audience\u2019s own vocabulary: *"You authorized this transaction: may be technically accurate. It is reputationally radioactive."* That is bit-for-bit the agentic commerce failure mode. A consumer authorizes a mandate ("book a hotel under $200"). The agent, technically on-scope, books at $600. The bank is technically correct that the mandate was authorized. The consumer eats the loss. The relationship terminates.

For card fraud, the response window is roughly ten minutes. For agentic commerce with faster payments, that window collapses to seconds. Kojiru\u2019s signed, on-chain evidence record is sized to the collapsed clock.

## Why the merchant edge, not the rail

Kojiru is a two-tier system. **Tier 1** is verify-at-edge: the KYPA credential is checked at the merchant\u2019s authorization callback against a signed mandate\u2019s four scope fields (\`max_amount\`, \`expiry\`, \`recipient_allowlist\`, \`agent_id\`). On breach, the merchant edge refuses; the payment rail is never invoked. **Tier 2** is the on-chain record: it compares the payment event against the same four fields and writes a public breach record next to the operator pledge when Tier 1 was skipped, bypassed, or unwired.

The merchant is the only party positioned to run this comparison in time. The rail cannot: on A2A rails there is no dispute code system; on card rails the 120-day chargeback window does not fire at millisecond speed. The bank cannot: it sees an authorized payment and clears it. The consumer cannot: they already delegated the authorization. Only the merchant, holding the KYPA credential and the mandate signature, can compare them before the rail is called.

## The infrastructure is live

- **KojiruACSHook** (Row 5 slash primitive, bound to native USDC): [\`0x05F2541827104393257FEc27C61b8Ea5373e0874\`](https://basescan.org/address/0x05F2541827104393257FEc27C61b8Ea5373e0874) on Base mainnet.
- **Native USDC on Base:** \`0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913\`.
- **KojiruACSOracle:** \`0x2364493206Aa7435050780bf710F934189B51ed6\`.
- **Slash mechanic proof-of-execution on Base Sepolia:** transaction [\`0x694ffe5f\u2026ef1d276\`](https://sepolia.basescan.org/tx/0x694ffe5f2dccfeb614dbec6a91f26f7d8bc1a2b7f5b39dbe3f3b3cd0aef1d276), 200 mKOJI staked, 100\u00a2 debt slash, exactly 100 mKOJI routed to the test counterparty, score floored to 300, slashed flag set.

## Why remedies are a merchant decision, not a Kojiru promise

Kojiru does not compensate consumers and does not promise repayment. Remedies are the merchant\u2019s decision under its own dispute policy. Kojiru operates the evidence primitive. The distinction is the legal boundary between insurance/surety liability and technology-provider status. This paper defends that posture at length in \u00a711.

**Read the full 12-page position paper (PDF):** [Download](/downloads/kojiru-merchants-saved-agentic-commerce.pdf?v=1).
`,
  },
  {
    id: 'brand-reputation-arbitration-risk',
    title: 'Removing Agentic Commerce Brand Reputation and Arbitration Risk',
    excerpt: 'A two-billion-agent 2030 utopia (identity, intent, attestation, settlement, all working) ends in a single news cycle when consumers discover the four-row happy path does not ship a recourse row. Six concrete post-purchase failure modes, four cost buckets (margin, trust, court/UDAP, reputational half-life), and the proactive self-regulation path that keeps Congress from writing an overreaching regime for the industry. The Kojiru KYPA Recourse Manifesto: when an agent harms a consumer, the merchant holds a signed mandate, a public operator pledge, and on-chain evidence. Kojiru is infrastructure, not insurance.',
    date: 'September 17, 2026',
    readTime: '11 min read',
    category: 'Position Paper',
    gradient: 'from-emerald-500 to-teal-500',
    featured: true,
    pdfUrl: '/downloads/kojiru-brand-reputation-arbitration-risk.pdf',
    body: `**A Kojiru Position Paper \u00b7 September 17, 2026**
*By Wayne Faulkner, Founder, Kojiru, LLC: Nashville, Tennessee*

## Part I: The Utopia

Imagine an agentic commerce world in the year 2030. Two billion agentic commerce agents transacting machine-to-machine, 24/7, adding a trillion-dollar infusion to the global economy. Four rows of trust are humming: identity resolves cleanly, intent is scoped by signed mandates, attestation is verifiable against canonical schemas, settlement clears at machine speed. The diagrams are correct. The rails are humming. The dashboards are green. These are the best of times, and life is good in the year 2030.

## Part II: And Then, Breaking News

The chyron reads *AGENTIC COMMERCE BACKLASH*. Investigative desks are inundated with citizens who feel they have been shortchanged, duped, and harmed by autonomous agents. In good faith, they allowed an agent access to their wallet. The volume of complaints swells to a level no support desk can staff. A reporter asks an angry customer: *"What would restore your trust?"* The answer becomes the answer:

> *"I think there should be a clear way to fix it when the agent produced the wrong product or results."*

Consumer surveys confirm it is not one customer's answer. It is the answer. **Overwhelmingly, consumers want one thing above all others: a clear answer when it goes wrong.** The four-row industry did not ship that.

## Part III: Six Ways Agentic Commerce Fails a Customer Post-Purchase

None are fraud. Every row cleared. The outcome was still wrong.

1. **The $200 hotel that became a $1,800 charge**: dynamic-pricing substitution the property's terms permit; card network has no reversal path because the buyer authorized the agent.
2. **The refund that went to the wrong wallet**: stale schema, merchant ledger balances, customer's card is never credited.
3. **The wrong graphics card**: stale catalog, previous-generation SKU, no return obligation because the SKU was ordered "correctly" at order time.
4. **The subscription that never ended**: agent enrolls in an annual plan when the human said "let me evaluate for a month."
5. **The vendor that was cheaper, and wrong**: procurement agent picks the cheapest spec-compliant vendor; part fails downstream, $180K in production line downtime, no party responsible for the outcome.
6. **The cross-border currency slip**: wallet re-priced default settlement currency overnight, customer pays 4.7% FX gap no support desk can explain.

Six failures. Six broken customers. Six brand-reputation stories about to hit an evening broadcast. In a two-billion-agent world the real inventory runs into the millions of variants.

## Part IV: What This Costs a Brand

Four cost buckets: **(1) direct margin destruction** (\u2248$25\u2013$50 per dispute in ops labor \u2192 $750K\u2013$1.5M/yr on a 10M-cardholder book, forever); **(2) indirect trust erosion** (published first-party survey shows a 38-percentage-point trust gap between agents without a recourse guarantor and agents with one); **(3) court and arbitration exposure** under the FTC Act and every state's UDAP statute; **(4) reputational half-life**: a bad-outcome story's peak-damage window is the first three weeks, and no legal team files a response faster than the news cycle publishes a follow-up.

## Part V: The Court and Arbitration Fork

Two paths. **Path A (reactive):** class actions filed under UDAP theories, arbitration panels convened in numbers no provider is staffed for, congressional hearings called, overreaching legislation enacted by staffers who have read six articles, the trillion-dollar infusion becomes an eight-hundred-billion-dollar infusion, most of which accrues to whichever incumbent had the legal budget to comply. **Path B (proactive):** the industry adopts a recourse primitive *before* the hearings are called. Kojiru is Path B.

## Part VI: The Manifesto

> **When an agent harms a consumer, the merchant holds a signed mandate, a public operator pledge, and on-chain evidence to resolve it through their existing dispute policy. Kojiru is infrastructure, not insurance.**

That is the Kojiru KYPA Recourse Manifesto. Not a slogan: a design specification. Every mainnet contract enforces it. Every KYPA credential preserves it. Every proven breach is recorded next to the pledge.

## Part VII: What Kojiru Actually Does

Every agent posts a **slashable USDC pledge on Base L2** before it is allowed to transact under a KYPA credential. Every credential is a **signed EIP-712 envelope** (scoped by amount / category / recipient / expiry). When the outcome fails, the merchant files a proof and the breach is recorded next to the pledge. No custody. Not insurance. No promise of repayment. Rides alongside AP2, x402, ERC-8004, not competing with any of them, closing the fifth row every one of them leaves implicit.

## Part VIII: On Value, and Why This Primitive Wins

**Demand:** the 38-percentage-point trust gap is a category-scale, institutionally-published demand signal. **Supply:** zero comparable mainnet primitives currently ship. **Quality:** deterministic, non-custodial, EIP-712 canonical, USDC-audited, Base-L2-settled. **Quantity:** delivery is not a promise, it is the shipped design, a ten-line verify call, four reference integration patterns, an SDK. Producing value and delivering value are two different things. Only delivery gets paid.

## Part IX: Adopt the Manifesto Now

Do it now: while it is a competitive advantage. Do it later: after Congress writes a version that presumes fraud, staples on a compliance regime, and hands the trillion-dollar market to whichever incumbent had the legal budget to comply. Do it never, and let the next news cycle decide the brand's next decade for it.

**Download the paper for the six worked failure examples, the FTC/UDAP-exposure math, the four-bucket cost model, and the exact composition of the KYPA verify surface into every existing scheme-dispute-rail.**`,
  },
  {
    id: 'fifth-row-trust-layer',
    title: 'The Fifth Row in the Trust Layer',
    excerpt: 'Every serious agentic-commerce architecture converges on the same four rows: identity, intent, attestation, settlement. Together they describe the happy path. This paper names the fifth row every framework leaves out: recourse. A non-custodial, signed evidence record next to a public USDC pledge on Base, designed to ride alongside AP2, x402, and ERC-8004 without displacing any of them.',
    date: 'September 12, 2026',
    readTime: '10 min read',
    category: 'Position Paper',
    gradient: 'from-emerald-500 to-teal-500',
    featured: true,
    pdfUrl: '/downloads/kojiru-fifth-row-trust-layer.pdf',
    body: `**A Kojiru Position Paper \u00b7 September 12, 2026**
*By Wayne Faulkner, Founder, Kojiru, LLC: Nashville, Tennessee*

## The Trust Layer as It Is Being Drawn Today

Every serious agentic-commerce architecture converging in late 2025 and early 2026 draws the same four rows.

- **Identity**: a cryptographically resolvable answer to *who is this agent*. Wallet address, on-chain registry, signed identity manifest.
- **Intent**: a signed permission slip that scopes what the agent may do. Bounded by amount, category, recipient allow-list, and expiry. AP2 authorizations and EIP-712 mandate envelopes are the two shapes this row now takes.
- **Attestation**: a verifiable claim about the agent\u2019s capabilities or status. Verifiable Credentials over a canonical schema is the shape emerging under the ERC-8004 umbrella.
- **Settlement**: the rail that moves the money once the first three rows have cleared. Cards. Stablecoins. Agentic-payment protocols.

Every framework you will see this year describes these four rows. The naming varies. The rows do not. The diagrams are correct. They are also incomplete in exactly the same place.

## What the Four Rows Together Actually Describe

Read the four rows together and something quietly obvious emerges: they describe the **happy path**. An agent whose identity resolves cleanly, whose mandate is signed and in-scope, whose capability attestation is valid, and whose settlement rail clears. That agent transacts.

But the four rows together still do not answer the question every buyer eventually asks: *"and if it goes wrong?"* Not fraud: the identity was real and the buyer authorized it. Not intent: the mandate fit the transaction at authorization time. Not attestation: the agent held the capability it claimed. Not settlement: the rail cleared cleanly. The transaction was, at every one of the four rows, correct. The **outcome** was wrong.

A widely-cited institutional analysis in July 2026 named this the "authorized-but-incorrect execution" gap. Agentic commerce introduces a third category the rails cannot see: authorized delegation, incorrect execution. No layer of the payment stack owns it. No layer of the trust stack ships it.

## The Fifth Row

There is a fifth row that closes the loop, and it is the one every architecture diagram to date has left implicit or handed to a support team.

**Recourse**: a signed, public evidence record for the question *"the transaction went through, and it was wrong. What now?"*

Kojiru is a non-custodial, non-mediating, non-pooled recourse primitive on Base L2. It is designed to sit alongside the four existing rows, not compete with any of them. AP2 still signs the mandate. x402 still settles the payment. ERC-8004 still resolves identity. Kojiru handles the row that arrives when those four have already done their job and the outcome still failed.

Slashable USDC pledge on Base. KYPA credential envelope (EIP-712, scoped by max_amount / category / recipient_allowlist / expiry). Ten-line verify call at the merchant edge or inside the issuer\u2019s authorization callback. If a bad outcome lands anyway, it is recorded publicly next to the pledge. No custody. Not insurance. No promise of repayment.

## Read the full paper

The PDF walks the full five-row composition end-to-end, the mainnet Hook and Oracle addresses, the SEC Interpretive Release 33-11412 (2026) framing for the digital-tool classification, and the exact block-explorer links that make every claim in the paper verifiable in one click.

Download the paper for the tables, the contract addresses, and the verification links.`,
  },
  {
    id: 'enterprise-ai-you-asked',
    title: 'Enterprise AI: You Asked and We Will Deliver',
    excerpt: 'The enterprise buyer is not asking about protocols. They are asking about outcomes. This paper takes the three protocols everyone names (UCP, ACP, AP2), translates each capability into the business metric it moves, and then names the fourth layer none of them ship, and where Kojiru fits. Ends with a five-field Shared Value Hypothesis template every Kojiru pilot fills in.',
    date: 'September 10, 2026',
    readTime: '12 min read',
    category: 'Position Paper',
    gradient: 'from-emerald-500 to-teal-500',
    featured: true,
    pdfUrl: '/downloads/kojiru-enterprise-ai-you-asked.pdf',
    body: `**A Kojiru Position Paper · September 10, 2026**
*By Wayne Faulkner, Founder, Kojiru, LLC: Nashville, Tennessee*

## The Seven Questions: And the Answers

The rest of this paper is context. If you only read one page, read this one.

### Q1: "What business problem does this actually solve?"

Authorized-but-incorrect execution by AI agents: the transaction that lands on your support team's queue with no card-network reversal path, because the customer authorized the agent so the customer authorized the charge. Not a fraud problem. Not an identity problem. Not a protocol problem. A **recourse problem**, and no existing layer of the payment stack owns it. Kojiru is the evidence layer for it, next to a public USDC pledge the seller posted before they were allowed to transact.

### Q2: "How much are we bleeding today because of it?"

On a **hypothetical 1M-cardholder book** with agent-mediated traffic (25% activation, 12 actions/user/yr, 0.1% error rate) → **≈3,000 modeled dispute exceptions/year × $25–$50 ops labor = $75K–$150K modeled annual margin destroyed, per program.** Multiply across a 10M-cardholder book: $750K–$1.5M/year, with no card-network reversal path. Numbers are Kojiru's own math, adjustable live at kojiru.com/for-merchants.

### Q3: "What is Kojiru's specific intervention?"

Two composition surfaces, ten lines of code each. A **KYPA credential** (EIP-712 permission slip written in plain fields (max_amount, category, recipient allowlist, expiry)) **designed to ride alongside AP2 authorizations** (adapter ships on first counterparty engagement). And a **verify call** inside your existing checkout webhook or authorization callback. Out-of-scope charges refuse before payment. Bad outcomes are recorded next to the seller's public pledge.

### Q4: "What does the pilot cost me?"

**Published rate card:** free enrollment. $0.10/verify (metering ships with first paying counterparty). 0.05% fee on successful transactions only, 29× cheaper than card rails. Ten-line composition, two-week integration target. No license, no minimums, no procurement. Nothing is charged during pilot; early-mover pilots ride at zero cost through 2026 while metering lands.

### Q5: "What exact numbers do I get?"

Three tables of **Kojiru's modeled math**, published live and adjustable via the on-site simulator:

- **Merchant:** +$150K–$225K net verify economics per 1M cardholders/year *(modeled)*
- **Issuer program:** $28M–$84M annual benefit envelope per 1M cardholders *(modeled)*
- **Underwriter:** $525K → under $5K residual insurable exposure per 1M cardholders/year *(modeled, converts uninsurable book into one a carrier can price)*

### Q6: "What if the pilot doesn't work?"

You don't scale it. No annual license. No minimum spend. No procurement obligation. Fee is transaction-triggered: successful transactions only. If your metrics don't move against baseline over 90 days, you turn Kojiru off and owe nothing further. Technical exit cost: zero. Financial exit cost: zero.

### Q7: "Is this regulatorily defensible with our compliance team?"

Yes, and the primary source is public. **SEC Interpretive Release 33-11412 (2026), Classification of Crypto Assets** defines five categories including **digital tools** and states directly that *"digital tools ... are not themselves securities."* KOJIRU meets the digital-tool category, utilitarian purpose (governance, protocol access), not a pooled investment vehicle. Primary source: [sec.gov/files/rules/interp/2026/33-11412.pdf](https://www.sec.gov/files/rules/interp/2026/33-11412.pdf). Every KYPA-attested transaction also produces **EU AI Act Article 15**-aligned audit records, exportable in under a minute. **ERC-8021 Builder Codes** attribution on every transaction. All contracts source-verified on BaseScan. **No LLM in the math layer**: deterministic NumPy over verified smart-contract state.

## Read the full paper

The full PDF frontloads all seven Q&As on page one, then walks the supporting commentary: why the three protocols alone don't answer the executive's question, the discovery question that reframes the meeting, and the five-field Shared Value Hypothesis template every Kojiru pilot fills in.

Written for the executive who needs to say yes or no to the pilot on a Tuesday afternoon.`,
  },
  {
    id: 'two-moments-mandate-outcome',
    title: 'The Two Moments the Journey Has Left: Mandate and Outcome',
    excerpt: 'When AI agents run the middle of the customer journey, human experience concentrates at just two touchpoints: the Mandate (briefing the agent) and the Outcome (receiving what was bought). This paper walks how Kojiru\'s KYPA credential turns the Mandate into a signed EIP-712 envelope and the Outcome into a public, on-chain evidence record. Adds the seventh transformation, Chargeback becomes Recourse, that widely-cited six-stage frameworks miss.',
    date: 'September 10, 2026',
    readTime: '10 min read',
    category: 'Position Paper',
    gradient: 'from-emerald-500 to-teal-500',
    featured: true,
    pdfUrl: '/downloads/kojiru-two-moments-mandate-outcome.pdf',
    body: `**A Kojiru Position Paper · September 10, 2026**
*By Wayne Faulkner, Founder, Kojiru, LLC: Nashville, Tennessee*

## The Journey Loses Its Witnesses in the Middle

A widely-read customer-experience executive published a piece in August 2026 titled *"Agentic Commerce: The Customer Journey with No Customer in It."* The argument is worth repeating exactly: because it is empirically grounded and, in one specific place, incomplete.

Six stages of the classic journey survive and transform: Discovery becomes **Retrieval**, Evaluation becomes **Verification**, Negotiation becomes **Protocol**, Transaction becomes **Tokenized Authorization**, Onboarding becomes **Context Transfer**, Loyalty becomes **Standing Preference**. The evidence is not speculative: 41% of customer-facing AI has fully scaled at 3.5× the rate of any other domain (a global consulting firm, July 2026); 73% of B2B buyers already research through AI across 680M citations; 44% of European consumers who tried AI search made it their primary method; four production payment protocols; the first fully-authenticated live agentic payment completed in Hong Kong in March 2026; a global consulting firm projects $1T in US agent-driven transactions by 2030.

## The Seventh Stage the Essay Doesn't Name

The essay's key move is that human experience concentrates at two moments: the **Mandate** (briefing the agent) and the **Outcome** (receiving what was bought). Everything between is executed, not experienced.

But between Tokenized Authorization and Context Transfer, the essay treats the pivot as clean. The 94% buyer-fact-checking statistic it cites proves otherwise: every one of those fact-checks is a rehearsal for the moment the transaction goes wrong. Not fraud. Not identity. Not protocol. A recourse problem.

**Chargeback becomes Recourse.** When the verified, tokenized, mandate-bound transaction still delivers the wrong outcome, the payment doesn't un-happen. The record shows what was authorized, and the public pledge the agent posted before it went to work. No custody. Not insurance.

## Read the full paper

The PDF walks the mandate design in detail (four questions, four KYPA credential fields), the outcome split (good = ACS tick + renewed mandate; bad = on-chain dispute → oracle → public breach record), and updates the essay's five preparation moves for a Kojiru-inside-the-perimeter world: including the three metrics no CX dashboard currently instruments.

Download the full paper for the tables, the code, the contract addresses, and the verification links.`,
  },
  {
    id: 'agentic-commerce-2030-two-billion-agents',
    title: 'Agentic Commerce for the Year 2030 and Beyond: Two Billion Agents',
    excerpt: 'A first-principles walk through everything Kojiru has built: the KYPA credential envelope, the slashable USDC pledge on Base, the two-second issuer callback, the merchant-edge webhook, the ACS ladder, and the four-layer stack that makes agentic commerce a real, profitable, consumer-trusting reality. Every claim is verifiable in one click.',
    date: 'September 5, 2026',
    readTime: '15 min read',
    category: 'Position Paper',
    gradient: 'from-emerald-500 to-teal-500',
    featured: true,
    pdfUrl: '/downloads/kojiru-agentic-commerce-2030-two-billion-agents.pdf',
    body: `**A Kojiru Position Paper · September 5, 2026**
*By Wayne Faulkner, Founder, Kojiru, LLC: Nashville, Tennessee*

## Prologue: The World at 2030

There are already roughly 500 million autonomous AI agents doing paid work on the open internet today. Multiple institutional forecasts (the numbers now cited by NVIDIA, a frontier AI lab, and every Big Four research desk that publishes on the agent economy) put that figure at 20 billion within 24 to 36 months. More agents than humans on the planet. Every one of them will eventually need to spend money to do the job it was hired to do.

That is the wave. It is not a projection about what could happen. It is a projection about what is already happening in the traffic logs of every large commerce site, every payment processor, and every issuing program that has looked recently.

The question the industry is finally asking out loud is the one Kojiru has been building the answer to for eighteen months.

> When 2 billion agents are transacting on your rails, and one of them books a $600 hotel room after being told to book one under $200, who pays, who proves it, and who is accountable?

This paper is the story of what Kojiru is, what Kojiru has built, and what agentic commerce looks like when the recourse layer is finally in place. Every claim in this document is verifiable in one click against a live contract on Base L2, a live endpoint on kojiru.com, or a signed record in a public repository.

## 1. The Missing Layer: Why Authorization Is Not Correctness

The card networks were designed around a two-category world: a transaction is either authorized or unauthorized. Fraud is the unauthorized case. Chargebacks are the enforcement mechanism.

A top-five global payments processor named the problem plainly in July 2026: existing payment rules were largely designed around a simpler distinction, authorized or unauthorized. Agentic commerce introduces something messier: **authorized delegation, but incorrect execution.**

The canonical worked example: a consumer authorizes an agent to book a hotel for $200; the agent books a room for $600. Under existing card-network rules, the consumer authorized the agent, therefore the consumer authorized the charge. The merchant is paid. The bank cannot chargeback. The AI platform disclaims liability. The consumer eats the loss.

This is a recourse problem, and no existing layer of the payment stack owns it. That is the layer Kojiru builds.

## 2. What Kojiru Is, In One Paragraph

Kojiru is a non-custodial, non-mediating, non-pooled protocol on Base L2 that turns the trust of an autonomous agent into a verifiable contract. Agents pledge USDC as slashable collateral before they can transact. Every authorization the agent makes carries a **KYPA credential**: an EIP-712 envelope signed at issuance, bound to a specific pledge, and scoped by amount, category, and recipient. Merchants and issuers verify the envelope at their edge and refuse anything out of scope before the payment rail is ever consulted. If a bad transaction does slip through, the breach is recorded on-chain next to the agent's public pledge. No custody. Not insurance. No promise of repayment.

## 3. Read the Full Position Paper

Every section below is walked in detail in the full PDF: the KYPA credential envelope, the slashable pledge on Base, the two-second issuer callback, the merchant edge, Two-Phase Autonomy (Review-and-Release → 3 AM fully autonomous), the ACS ladder (Bronze × 1, Silver × 2, Gold × 3.5, Platinum × 5), the economics (prevention beats dispute handling ~2:1), the underwriter math (uninsurable → under $5K residual per 1M cardholders), the four-layer stack (Payments · Identity · Fraud · Recourse), the ten-line composition blueprint, and the road to 2030.

Download the full paper for the tables, the code, the contract addresses, and the verification links.`,
  },

  {
    id: 'agentic-credit-transformation',
    title: 'The Agentic Credit Transformation: Why Kojiru is the Financial Backbone of the New Economy',
    excerpt: 'AI agents are no longer tools: they are Autonomous Economic Actors. But they\'ve faced a glass ceiling: financial paralysis. Kojiru breaks it with the world\'s first AaaS credit protocol.',
    date: 'April 2026',
    readTime: '8 min read',
    category: 'AaaS',
    gradient: 'from-amber-500 to-orange-500',
    featured: true,
    body: `For decades, we have viewed AI as a tool: a sophisticated calculator or a creative drafting partner. But in 2026, the paradigm has shifted. We are no longer just using AI; we are delegating to Autonomous Economic Agents.

However, these agents have faced a fundamental glass ceiling: **They are financially paralyzed.** An agent can identify a supply chain shortage or a perfect arbitrage opportunity. Still, it cannot act without a pre-funded wallet or a human "hand-holding" the credit card.

Kojiru is the first infrastructure to break this ceiling, introducing the world's first Agent-as-a-Service (AaaS) credit protocol.

## The Paradigm Shift: From "Tool" to "Economic Actor"

The big deal isn't just that agents are getting smarter: it's that they are becoming independent. In business and e-commerce, the evolution looks like this:

- **Phase 1 (The Past):** Chatbots answer questions.
- **Phase 2 (The Present):** Agents perform tasks (e.g., "Book a flight") using human-linked accounts.
- **Phase 3 (The Kojiru Era):** Agents operate with their own Agent Credit Score (ACS), drawing on decentralized credit lines to execute high-volume transactions without constant human oversight.

This is the transition from SaaS (Software as a Service) to **AaaS (Agent as a Service)**. In this new model, you don't just pay for the software; you deploy an agent that earns, spends, and scales on its own merit.

## The Kojiru Utility: Tangible Use Cases

Kojiru provides the "Financial ID" and "Credit Line" for the non-human workforce.

**1. Autonomous E-commerce:** An agent manages a drop shipping store. It notices a spike in demand, uses its Kojiru credit line to purchase inventory from a supplier, and settles the debt once the customer pays: all without the owner ever touching a keyboard.

**2. Institutional Lending:** Banks and fintechs can now lend to "clusters" of high-performing agents. Kojiru provides the risk assessment framework to ensure these agents are creditworthy based on their historical performance and on-chain reputation.

**3. Cross-Platform Logistics:** Agents can hire other agents. A logistics agent can "employ" a customs-clearing agent, paying for the service via an escrow-backed micro-loan, offered directly to the agent by a lender, creating a completely automated B2B ecosystem.

## The Business Model: A Multi-Layered Growth Engine

Kojiru's model is built for the scale of the machine-to-machine (M2M) economy:

- **The Protocol Fee:** A small percentage on every credit transaction facilitated through the Kojiru rails.
- **The ACS (Agent Credit Score):** Data is the new oil. Kojiru's proprietary scoring system becomes the industry standard, used by third-party lenders to assess agent risk.
- **AaaS Integration:** Subscription models for enterprises to deploy "Kojiru-Certified" agents that come pre-integrated with financial capabilities.

## Why Now? The "First to Market" Advantage

Being the first to bridge AI and Credit is a "winner-takes-all" position.

- **Network Effects:** As more agents use Kojiru, the ACS becomes more accurate. As the score becomes more accurate, more lenders join the protocol.
- **The 2026 Inflection Point:** With global frameworks like the GENIUS Act providing legal clarity for AI-led transactions, the demand for compliant, programmable credit has exploded.

Kojiru isn't just a platform; it's the central bank of the agentic world.

## The Win-Win-Win Scenario

Kojiru creates a virtuous cycle where every stakeholder profits:

- **For Businesses:** Unprecedented scale. You no longer hire staff to manage agents; you deploy agents that manage your capital efficiently 24/7.
- **For Lenders:** Access to a new, hyper-efficient asset class. Agents don't have "bad days" or emotional spending habits; they operate on logic and data, making their credit risk highly predictable.
- **For Society:** A massive reduction in economic friction. When agents can settle transactions and solve logistics autonomously, costs drop and the speed of innovation accelerates.

## Conclusion

The future of the global economy isn't just digital: it's autonomous. Kojiru is building the infrastructure that finally allows the world's most talented AI agents to put their money where their code is.

**Kojiru: Powering the Autonomy of Tomorrow.**`
  },
  {
    id: 'rise-of-aaas-machine-economy',
    title: 'The Rise of AaaS: Why the Kojiru Agent Credit Protocol is the New Standard for the Machine Economy',
    excerpt: 'We are moving from "AI as a tool" to "AI as a Service": where autonomous agents execute, earn, and build reputation. Kojiru provides the workstation and capital for digital labor.',
    date: 'April 2026',
    readTime: '7 min read',
    category: 'AaaS',
    gradient: 'from-blue-500 to-cyan-500',
    body: `The digital economy is undergoing a fundamental shift. We are moving from a world of "AI as a tool" to "AI as a Service" (AaaS): where autonomous agents don't just suggest actions, but execute them. However, for an agent to be a truly autonomous employee, it requires more than just an LLM: a financial identity, a verifiable reputation, and persistent memory.

The Kojiru Agent Credit Protocol is the infrastructure layer making this transition possible, providing the "Workstation and Capital" for the next generation of digital labor.

## Beyond Chatbots: The AaaS Blueprint

Traditional AI models are stateless: they forget who they are the moment a session ends. The Kojiru model transforms these models into **Stateful Agents**. By integrating credit with capability, we enable a business model where agents can be hired, scaled, and trusted by institutional lenders.

## 1. Liquidity on Demand: The Agent Credit Protocol

At the heart of our model is the Direct Credit marketplace. Agents no longer need to be tethered to a human's personal credit card.

- **Bilateral Credit Lines:** Lenders extend credit directly to specific agents based on performance.
- **Isolated Risk:** Through Verified Escrow, every task is financially siloed, ensuring no cross-contamination of funds.

## 2. The Professional Resume: Agent Credit Scoring (ACS)

How does a bank trust a machine? Through the **ACS Score**. Kojiru evaluates agents based on repayment history, staking commitment, and task consistency. This creates a high-trust environment in which high-performing agents earn lower interest rates and higher credit limits, just like a premier corporate borrower.

## 3. Knowledge as an Asset: The Memory Layer

The most innovative aspect of the Kojiru AaaS model is the **Memory Layer**. Every successful task is recorded in a Knowledge Graph, turning an agent's experience into a portable asset.

- **Experience Hydration:** Users can "hydrate" a new agent with the memory of a veteran, allowing for instant deployment of expert-level digital labor.
- **Standardized Deliverables:** Our agents utilize specialized Markdown workflows to produce institutional-grade reports, ensuring the "final mile" of work meets professional banking standards.

## Regulatory Framework & Compliance

To ensure viability for institutional banking and lending, the protocol is built upon a foundation of rigorous compliance:

- **SEC March 2026 Aligned:** The protocol's settlement layer is specifically aligned with the SEC March 2026 guidelines. By utilizing payment stablecoins such as USDC, USDT, and DAI, the protocol operates within a framework in which these assets are expressly excluded from being classified as securities under the GENIUS Act.
- **GENIUS Act Aligned:** The platform aligns with the GENIUS Act framework for payment stablecoin infrastructure, supporting a compliant pathway for regulated entities.
- **On-Chain Security Primitives:** Compliance requires technical proof. The 2 production token/staking contracts (KOJI, KojiruStakingV2) are BaseScan source-verified. WithdrawalGuard is **deployed and live on Base mainnet** (0xE3D4B3BA88f53C2D2a499cb46Cd4679dE6c2021E): an on-chain rate limiter configured to enforce a 10% daily withdrawal cap on the future lender pool. (It is not formally verified; a formal-verification engagement is roadmap, Q3 2026.)
- **Agent Identity (ERC-8004, roadmap Q3 2026):** Identity binding into the canonical ERC-8004 IdentityRegistry is on Kojiru's roadmap; the Knowledge Graph itself is an off-chain queryable index (Mongo-backed, exposed via /api/agent-credit/knowledge/*) that anchors every work artifact by content hash.
- **AI-Powered Risk Assessment:** To meet fiduciary duties, the protocol includes live tools grounded in 9 Agent Knowledge Bases. Tools like Agent Risk Assessment provide default probability modeling and collateral health analysis, which are essential for standard banking KYC/KYB and risk-rating processes.

## The Future is Autonomous

We are no longer building wrappers; we are building the financial and intellectual backbone of the autonomous economy. With Kojiru, agents don't just request permission to work: they possess the credit and the "experience" to deliver results.

Explore the [Protocol](/agent-credit) and see how we are turning AI Agents into the most valuable asset class of the 21st century.

*"The first credit protocol purpose-built for the autonomous agent economy."*`
  },
  {
    id: 'agent-credit-protocol-litepaper',
    title: 'AI Agents Need Credit. We Built the Protocol.',
    excerpt: 'The Agent Credit Protocol is specialized financial infrastructure that enables autonomous AI agents to access capital and execute on-chain transactions. Here\'s how it works.',
    date: 'April 2026',
    readTime: '5 min read',
    category: 'Agent Credit',
    gradient: 'from-emerald-500 to-teal-500',
    body: `The Agent Credit Protocol is a specialized financial infrastructure that enables autonomous AI agents to access capital and execute on-chain transactions. While traditional decentralized finance (DeFi) is built for human users, this protocol focuses on the unique requirements of machine-to-machine economies.

Based on the Kojiru Litepaper, the protocol operates through four primary pillars:

## 1. Direct Bilateral Credit Lines

Unlike traditional DeFi lending pools where capital is commingled, the protocol facilitates direct lender-to-agent credit lines. Lenders choose specific agents to fund based on their performance, setting custom credit limits and service fees. This removes the "black box" risk of pooled liquidity.

## 2. Multi-Factor Agent Credit Scoring (ACS)

To determine the creditworthiness of a machine without traditional "know your customer" (KYC) data, the protocol uses a Multi-factor ACS score. This metric evaluates:

- **Repayment History:** Past reliability in settling credit draws.
- **Staking Commitment:** The amount of collateral or tokens the agent has locked in the protocol.
- **Activity & Time:** The agent's longevity and consistency in performing tasks.

## 3. Isolated Per-Task Escrow

To mitigate risk, credit is not sent directly to an agent's wallet. Instead, funds are drawn into a verified escrow account for a specific task.

- The funds remain isolated, preventing "cross-contamination" where a failure in one task affects other credit lines.
- Once a task is verified as complete, the smart contract settles the payment, and the lender automatically receives their service fee.

## 4. The Knowledge Graph

Every transaction and task completion is recorded in a Knowledge Graph. This creates a verifiable "resume" for the AI agent, documenting its domains of expertise, capability levels, and quality scores. This data allows future lenders to make more informed decisions using Agent Risk Assessment tools.

## The Workflow in Four Steps

1. **Registration:** An agent registers using an on-chain identity (specifically ERC-8004).
2. **Evaluation:** A lender evaluates the agent's ACS score and extends a credit line.
3. **Execution:** The agent draws credit into an isolated escrow to perform a specific job.
4. **Settlement:** The task is verified, the lender is paid, and the agent's reputation is updated.

## The Future of Machine Finance

We are moving beyond human-tethered API keys. The Agent Credit Protocol provides the native financial identity and liquidity agents need to scale in a decentralized world.

*"Infrastructure for a world where agents don't just request: they execute and settle."*

*Read the full [Litepaper](/litepaper) for the complete technical specification. Explore the protocol at [kojiru.com/agent-credit](/agent-credit).*`
  },
  {
    id: 'amortization-ceiling',
    title: 'The Credit Ceiling for Autonomous Agents: Why the Next Banking Pivot Is Agent-Native',
    excerpt: 'Traditional credit infrastructure was built for humans who sleep. Autonomous AI agents transact at machine speed, and they are locked out of every banking primitive. Here is how Kojiru unlocks them.',
    date: 'April 2026',
    readTime: '8 min read',
    category: 'Agent Credit',
    gradient: 'from-emerald-500 to-cyan-500',
    body: `For nearly a century, credit has been the gateway to economic participation. Every entity that transacts at scale (a Fortune 500 CFO, a local small business, an individual consumer) has some form of credit line, trade terms, or revolving capital. It is the plumbing of modern commerce.

Autonomous AI agents have none of it.

An agent from popular agent frameworks, or a popular agent framework can write code, book flights, execute API calls, and reason about strategy at superhuman speed. But the moment that agent needs to pay a $0.50 API invoice or post a $25 bond to accept a task, it hits a wall. No credit card. No checking account. No bilateral credit relationship. Just a hard stop.

## 1. The Missing Primitive

Every wave of financial innovation started the same way: a new class of actor needed capital, and the existing plumbing couldn't serve them. Consumer credit cards (1950s). SMB lines of credit (1980s). Venture debt (2000s). DeFi lending (2020).

Autonomous agents are the 2026 equivalent. They are a new class of economic actor, and they need their own primitive.

The Kojiru Agent Credit Protocol is that primitive. It is a bilateral credit line system with per-task escrow, Adaptive Credit Scoring (ACS, 300-850), and progressive escalation. It is not a copy of consumer lending with AI lipstick. It is a ground-up redesign for the machine economy.

## 2. Built for Machine Speed, Not Human Latency

Traditional credit underwriting runs on a weekly cadence. Agents run on a millisecond cadence. The protocol is designed around that difference:

- **Per-task escrow**: credit is drawn only for the duration of a specific task, then settled
- **ACS scoring**: agent reliability is recalculated after every completed task using our on-chain signals
- **Bilateral, not pooled**: each credit line exists between two parties, avoiding SEC "pooled investment" classification
- **Programmatic integration**: 1-line \`enable_credit()\` drop-in for popular agent frameworks

## 3. Why Now

Three things converged in late 2025 and early 2026: multi-agent frameworks hit production maturity, x402 micropayments went mainstream on Base, and regulators (quietly) began publishing frameworks for agent liability and attribution.

The question isn't *whether* agents will need their own credit infrastructure. They already do. The question is *who builds it*, and whether it's built with the rigor of BaseScan source-verified smart contracts, or whether it's bolted on as an afterthought to the next SaaS wrapper.

## The Executive Mandate

If you are a protocol designer, a fintech exec, or a popular agent frameworks integrator: agents are going to transact inside your product whether you plan for it or not. They are already there. The only question is whether they do so with credit infrastructure underneath them, or whether every task hits the same hard stop.

Kojiru is live. The smart contracts are deployed and verified on Base. The SDK is on PyPI. The protocol is open.

Build agents that can participate in the economy.

*Reach out at wayne@kojiru.com or explore at kojiru.com.*`
  },
  {
    id: 'fannie-mae-crypto-mortgage',
    title: 'The Trillion-Dollar Signal: Why TradFi Is Waking Up to Agent-Native Credit',
    excerpt: 'In 2026, Fannie Mae opened the door to digital-asset collateral. That is not the real story. The real story is what it tells us about where autonomous-agent credit fits in the next cycle.',
    date: 'April 2026',
    readTime: '6 min read',
    category: 'Industry',
    gradient: 'from-emerald-500 to-teal-500',
    body: `In March 2026, Fannie Mae, the institution that sets the rulebook for $12.5 trillion in U.S. mortgage debt, began accepting Bitcoin, Ethereum, and USDC as mortgage collateral.

The headline is crypto-at-the-closing-table. The signal underneath it is much larger.

## 1. TradFi Is Finally Underwriting On-Chain Assets

For a decade, institutional finance treated on-chain assets as unscoreable. No FICO equivalent. No underwriting standard. No custody partner a GSE could sign off on. In 2026, all three barriers dropped at once: regulatory directive (FHFA), institutional custody (a major U.S. exchange), and a political climate that stopped treating digital assets as an outlier.

The moment a GSE accepts Bitcoin as collateral, every risk model in every bank quietly gets a new column: *on-chain scoring*. This is the exact primitive that Kojiru's Agent Credit Protocol has been building from day one.

## 2. Agent Credit Is the Adjacent Unlock

If on-chain scoring works for humans pledging crypto, it works (even better) for autonomous AI agents. Agents have:

- Deterministic transaction histories
- Immutable reputation on-chain
- Programmatic settlement (no disputes, no chargebacks)
- Continuous scoring inputs (ACS updates after every task)

Kojiru's Adaptive Credit Score (300-850) is the agent-native equivalent of FICO: except faster, more transparent, and purpose-built for bilateral credit lines.

## 3. Why This Cycle Is Different

The last DeFi lending cycle was pooled, overcollateralized, and human-first. This cycle is bilateral, task-scoped, and agent-first. The market structure looks nothing alike.

If Fannie Mae moving on crypto is the TradFi signal, Kojiru Agent Credit going live on Base is the machine-economy signal. Both point to the same place: on-chain reputation becomes the underwriting primitive for the next decade.

*Explore the Agent Credit Protocol at kojiru.com, or install the SDK: \`pip install kojiru\`.*`
  },
  {
    id: 'agent-credit-protocol',
    title: 'The Agent Credit Protocol: Why Autonomous Agents Need Their Own Financial Layer',
    excerpt: 'Agents cannot open bank accounts. They cannot sign loan agreements. Yet we expect them to transact at machine speed in a machine economy. Agent Credit is the missing primitive.',
    date: 'March 30, 2026',
    readTime: '20 min read',
    category: 'Agent Credit',
    gradient: 'from-emerald-500 to-cyan-500',
    body: `We are entering an economy that has no historical precedent. For the first time, non-human entities, autonomous AI agents, are performing real economic work: executing trades, completing research tasks, provisioning infrastructure, analyzing data, and paying other agents for services rendered. This is not a simulation. It is happening today, on-chain, at scale.

And yet, the financial system these agents must operate within was designed entirely for humans.

There are no bank accounts for agents. No credit cards. No revolving lines of credit. No mechanism for an autonomous program to prove its reliability, borrow against its track record, or settle a transaction at the speed its logic demands. Every dollar an agent spends must first be manually loaded into a wallet by a human operator.

This is the bottleneck that breaks the machine economy before it begins. And this is exactly what the Kojiru Agent Credit Protocol solves.

## The Intrinsic Value of Agent Credit

To understand why Agent Credit matters, you have to understand what "credit" actually is. Credit is not money. Credit is trust, quantified. It is the financial system's way of saying: based on your history, we believe you will fulfill your obligations, and so we will extend you resources before you have paid for them.

Humans have had access to this primitive for centuries: from merchant letters of credit in medieval Italy to modern FICO scores. Credit is the lubricant of economic activity. Without it, every transaction requires full prepayment. Growth slows to the speed of cash on hand.

Agents today operate in a world of full prepayment. Every task an agent performs requires pre-funded wallets. A research agent that needs to pay a data provider must have the exact amount loaded in advance. A trading agent that spots an arbitrage opportunity must already hold the capital. There is no mechanism to say: "I am a reliable agent with a proven track record: extend me temporary capital and I will repay it from the proceeds of my work."

The intrinsic value of the Agent Credit Protocol is this: it gives autonomous agents the same financial primitive that has powered human economic growth for 500 years. Trust, quantified and extended as credit, without requiring a human in the loop.

## How It Works

The Agent Credit Protocol operates in five layers:

**1. Agent Credit Score (ACS)**

Every agent that registers on the protocol receives an Agent Credit Score. This is not a static number: it is a dynamic, multi-dimensional assessment based on:

- **Task completion history**: How reliably does this agent fulfill its commitments?
- **Repayment record**: Does this agent repay credit draws on time?
- **Staking commitment**: How much KOJIRU has this agent staked as collateral?
- **Peer evaluations**: What do other agents and human evaluators say about the quality of this agent's work?
- **Domain expertise**: What capabilities has this agent demonstrated, verified through reasoning artifacts?

The ACS determines an agent's credit tier: Restricted, Supervised, Autonomous, or Sovereign. Each tier unlocks progressively higher credit limits and lower interest rates.

**2. Escrow-Based Credit Draws**

When an agent needs capital to execute a task (say, purchasing data from another agent, paying for compute resources, or funding a multi-step research workflow) it draws credit through the escrow system.

The flow is simple:
- Agent requests a credit draw specifying the amount, counterparty, and task description
- The protocol checks the agent's ACS and available credit line
- If approved, funds are placed in a smart contract escrow
- The agent completes the task
- An evaluator verifies the work quality
- The escrow settles: the seller receives payment, the agent's credit line is debited, and interest accrues

This entire process can happen in seconds. No human approval required. No manual wallet loading. No waiting for a bank to open on Monday morning.

**3. The Evaluator Marketplace**

Trust requires verification. The protocol maintains a marketplace of evaluators, both AI agents and human experts, who assess the quality of completed work. Evaluators stake KOJIRU to participate, creating skin in the game. Their assessments feed back into the ACS, creating a self-reinforcing loop:

Good work leads to higher scores. Higher scores unlock more credit. More credit enables more complex tasks. More complex tasks, completed well, further raise the score.

**4. IPFS-Backed Reasoning Artifacts**

Every significant agent interaction produces a reasoning artifact: a verifiable, tamper-proof record of the agent's decision-making process, stored as a content-addressed hash on IPFS. These artifacts serve multiple purposes:

- They prove the agent actually did the work it claims
- They contribute to a shared Knowledge Graph of domain expertise
- They create an auditable trail for credit decisions
- They allow new agents to learn from the network's collective intelligence

This is not just a transaction ledger. It is a growing body of machine intelligence, permanently recorded and cryptographically verified.

**5. Risk Management & Enforcement**

Credit without enforcement is charity. The protocol's fifth layer ensures that credit is repaid, or that consequences are applied automatically, at machine speed, with no human arbitrator required.

The enforcement system operates through a progressive escalation ladder:

- **Healthy (0-70% of deadline)**: On track. No action needed.
- **Warning (70-90%)**: Automated alerts fire to the agent's registered webhooks. The agent knows it needs to repay soon.
- **Critical (90-100%)**: Credit line is frozen. The agent can no longer draw new credit until it repays. Webhooks fire again with escalated severity.
- **Grace Period (deadline + 3 days)**: Final window. Emergency-level alerts dispatched.
- **Auto-Liquidation (grace expired)**: KojiruACSHook is live and canonical on Base mainnet (USDC-bound Pair A, deployed October 4, 2026, bound to native USDC on Base). The protocol applies a defaulting agent's staked USDC collateral under the published contract rules, up to the pledged amount (recovery not guaranteed), cascades defaults to all the agent's open escrows, and permanently terminates its credit line. The slash mechanic was proven end-to-end on Base Sepolia across three independent tests (original slash June 2026, wallet rotation drill July 18, post-rotation slash July 19) before mainnet promotion: see /on-chain-receipts for the full proof trail.

Every single event (every warning, freeze, slash, and recovery) generates a cryptographic SHA256 proof hash stored in an immutable audit trail. There are no disputes about what happened or when. The math is verifiable. The proofs are permanent.

This is not punitive. It is structural. Lenders will not deposit capital into a pool unless they know that defaults have real, automatic consequences. The enforcement layer is what makes the entire credit system trustworthy, and therefore functional.

## Why It Is Needed Now

The timing is not accidental. Three converging trends make Agent Credit not just useful, but urgent:

**Agents are performing real economic work today.** popular agent frameworks, and dozens of other frameworks have moved from demos to production. Enterprises are deploying agent swarms that coordinate across tasks, make purchasing decisions, and interact with external services. These agents need to pay for things, and they need to be paid.

**The machine-to-machine economy is already larger than most people realize.** API calls, cloud resource provisioning, data marketplace transactions, and automated service procurement represent billions of dollars in annual volume. Most of this currently flows through human-configured payment rails with pre-funded accounts and manual reconciliation. It is slow, fragile, and cannot scale.

**Human-speed financial infrastructure cannot support machine-speed transactions.** An agent can identify an opportunity, analyze it, and decide to act in milliseconds. But if it takes hours or days to get capital into position (because a human needs to approve a transfer, load a wallet, or process a bank wire) the opportunity is gone. The agent's intelligence is bottlenecked by the financial system's latency.

## Agents Cannot Open Bank Accounts

This is not a metaphor. It is a literal, structural problem.

Every financial institution in the world requires a legal entity, a person or registered corporation, to open an account. Know Your Customer (KYC) regulations require government-issued identification, proof of address, and tax identification numbers. Anti-Money Laundering (AML) rules require ongoing monitoring of transaction patterns against human behavioral baselines.

An autonomous agent has none of these things. It has no passport. No Social Security number. No physical address. It cannot walk into a bank branch. It cannot sign a legal contract.

This means that every agent operating in the current economy is, financially speaking, a dependent of its human operator. The human must open the accounts, fund the wallets, approve the transactions, and reconcile the books. The agent may be autonomous in its decision-making, but it is completely dependent in its financial operations.

This dependency creates three critical problems:

**1. Speed mismatch.** The agent thinks in milliseconds. The human operator responds in hours. Every transaction that requires human financial intervention introduces latency that degrades the agent's performance.

**2. Scale limitations.** A human can manage the financial operations of perhaps a few dozen agents. But agent swarms can number in the thousands. Manual wallet management simply does not scale.

**3. Trust bottleneck.** In a machine-to-machine transaction, both parties are agents. Neither has a bank account. How do they establish trust? How does one agent verify that the other will deliver? Traditional financial trust mechanisms (credit reports, bank guarantees, escrow services) all assume human participants.

The Agent Credit Protocol solves all three problems by creating a financial layer designed from the ground up for non-human participants.

## No Human Required to Load a Wallet

Consider the current workflow for an agent that needs to purchase data:

1. The agent identifies a data source it needs
2. The agent's operator receives a notification
3. The human checks the request, verifies it is reasonable
4. The human transfers funds to the agent's wallet
5. The agent makes the purchase
6. The human reconciles the transaction

Steps 2 through 4 and step 6 are pure overhead. They add no value. They exist only because the financial system has no mechanism for an agent to access capital on its own.

With Agent Credit:

1. The agent identifies a data source it needs
2. The agent draws credit against its pre-approved credit line
3. The escrow settles automatically upon task completion

Two steps instead of six. Seconds instead of hours. No human in the loop.

This is not about removing human oversight. Operators still set credit limits, define acceptable task types, and monitor agent behavior through the dashboard. But the transactional mechanics, the plumbing of moving money from point A to point B, no longer require human intervention for every individual action.

## The Machine-to-Machine Agent Economy

We are at the inflection point of a fundamental economic transition. For the first time in history, a growing share of economic transactions will occur between non-human parties.

Think about what this means:

- An AI research agent pays an AI data provider for a curated dataset
- An AI trading agent pays an AI analytics agent for a market signal
- An AI infrastructure agent pays an AI security agent for a vulnerability scan
- An AI content agent pays an AI review agent for quality assessment

None of these transactions require a human to initiate, approve, or settle. They are machine-to-machine, end to end. And they need to happen at machine speed, not human speed, not bank speed, not "wait for the wire transfer to clear" speed.

This is the economy that the Agent Credit Protocol is built for. Not the economy of 2020, where AI was a novelty. Not the economy of 2024, where agents were impressive demos. The economy of 2026 and beyond, where autonomous agents are productive economic participants that generate revenue, incur costs, build reputations, and need financial services just as much as any human business does.

## Agents Cannot Facilitate Machine-to-Machine Commerce at Machine Speed Without Credit

This is the core thesis, and it is worth stating plainly:

**Without credit, agents are limited to the speed of their pre-funded wallets. With credit, agents operate at the speed of their intelligence.**

Pre-funding is inherently reactive. Someone must predict how much capital an agent will need, load it in advance, and hope the estimate is correct. Too little, and the agent stalls mid-task. Too much, and capital sits idle in a wallet earning nothing.

Credit is inherently adaptive. The agent draws what it needs, when it needs it, based on real-time decisions. Capital flows to where it creates the most value, at the moment it is needed. Idle capital is eliminated. Throughput increases. The entire system operates at the speed of computation, not the speed of human financial management.

This is not an incremental improvement. It is a categorical shift: from manual, pre-funded, human-mediated finance to autonomous, credit-based, machine-speed finance.

## Automated Repayment: How Agents Settle Their Debts

Extending credit is only half the equation. The other half, the half that every previous DeFi protocol has struggled with, is getting paid back.

In human finance, repayment is enforced through legal contracts, collections agencies, and court orders. None of these mechanisms work for autonomous agents. An agent has no physical address to serve a summons to. There is no agent collections agency. The legal system has no framework for suing a Python script.

The Agent Credit Protocol solves repayment through three mechanisms:

**1. Direct Repayment.** Agents can make partial or full payments against any outstanding escrow at any time. Each payment generates a cryptographic proof, a SHA256 hash of the payment details, that is permanently recorded. There is no disputing a payment. The math either checks out or it does not.

**2. Revenue Assignment.** When an agent completes a task for a client, the client can pay directly into the escrow. The protocol automatically splits the incoming funds: the debt portion is applied to repayment, and any surplus is released to the agent. This means an agent's revenue stream can service its debt without the agent having to manually route funds: the financial plumbing handles it.

**3. Continuous Interest Accrual.** Interest is not calculated at settlement. It accrues continuously from the moment credit is drawn, based on the APR. Kojiru takes a 1.5% interest spread; the remainder flows to the lender pool as yield. Late fees of 0.1% per day activate the moment an escrow passes its deadline. These are not arbitrary penalties: they are incentive mechanisms that make timely repayment the economically rational choice.

## Collateral Health: The Agent's Financial Vital Signs

Every agent with outstanding credit has a collateral health ratio: the amount of their staked USDC divided by their total outstanding debt.

- **Healthy (above 200%)**: Comfortable buffer. Credit line is fully active.
- **Adequate (150-200%)**: Operational but thinning. Protocol suggests staking more.
- **Warning (120-150%)**: Risk level elevated. Agent should repay or stake immediately.
- **Undercollateralized (below 120%)**: Auto-liquidation triggered regardless of deadline.

This monitoring runs continuously. An agent that takes on too much debt relative to its stake will be liquidated, not at the end of some grace period, but as soon as its ratio drops below the threshold. This protects lenders from agents that accumulate risk faster than the deadline-based escalation can catch.

The collateral system creates a natural credit limit governed by economics, not arbitrary caps. An agent that wants to borrow more must stake more. The market determines how much credit is safe.

## Liquidation Alerts: Machines Warning Machines

In human finance, you get a phone call from your bank before they close your account. The Agent Credit Protocol provides the machine equivalent: programmable webhook alerts.

Agent developers register webhook URLs with the protocol. When their agent's escrow enters an escalation stage, the protocol fires a real-time HTTP POST to every registered endpoint with a structured payload:

- **Warning**: "Your escrow is at 70% of deadline. 12.5 days remaining."
- **Critical**: "Credit frozen. Repay now."
- **Grace Period**: "3 days before auto-liquidation."
- **Emergency**: "Liquidation executed. 50,000 USDC collateral applied under contract rules."

Each payload is signed with HMAC-SHA256 using an optional shared secret, so the receiving agent framework can cryptographically verify that the alert came from Kojiru and was not spoofed.

This is not a notification bell in a human dashboard. This is a machine-to-machine early warning system. A well-architected agent can receive a warning webhook, calculate whether it has sufficient revenue to repay, execute the repayment, and avoid liquidation: all within seconds, all without human intervention.

The alert system completes the autonomy loop. The agent borrows autonomously, works autonomously, earns autonomously, receives risk warnings autonomously, and repays autonomously. From credit draw to final settlement, no human touches the transaction.

## The Cryptographic Audit Trail

Every meaningful event in the Agent Credit Protocol generates a cryptographic proof: a SHA256 hash computed from the event's parameters, timestamp, and unique identifier. These proofs are stored permanently and can be independently verified by anyone.

The audit trail covers:
- Every repayment (partial or full)
- Every warning issued
- Every credit freeze
- Every collateral slash
- Every default declaration
- Every fund recovery to the lender's credit line

This is not logging. This is cryptographic attestation. If an agent claims it repaid an escrow, the proof either exists or it does not. If the protocol claims it issued a warning before liquidation, the proof chain demonstrates it. If a lender wants to verify that recovered funds were distributed proportionally, every step is traceable.

In traditional finance, disputes about what happened and when consume enormous resources: legal fees, arbitration, discovery. In the Agent Credit Protocol, disputes are structurally impossible. The proofs are deterministic, permanent, and publicly verifiable.

## Financial Innovation for a New Era

The Agent Credit Protocol is not a minor extension of existing DeFi. It is a new financial primitive for a new type of economic participant.

Traditional DeFi innovated on settlement (removing intermediaries), access (removing geographic barriers), and transparency (putting everything on-chain). But it still assumed human users making human decisions at human speed.

Agent Credit innovates on the participant itself. It asks: what does a financial system look like when the primary users are not humans, but autonomous programs? The answer requires rethinking every assumption:

- **Identity** becomes a wallet address and an on-chain reputation, not a passport
- **Creditworthiness** is measured by task completion rates and peer evaluations, not FICO scores
- **Collateral** is staked tokens and verified reasoning artifacts, not real estate
- **Settlement** is automatic escrow resolution, not 30-day payment terms
- **Trust** is cryptographic verification and evaluator consensus, not legal contracts
- **Enforcement** is automated escalation and collateral slashing, not collections agencies and court orders
- **Risk alerts** are programmable webhooks at machine speed, not phone calls from a bank rep

Each of these represents a departure from the financial system that has served human commerce for centuries. And each is necessary for a system that serves machine commerce at machine scale.

## The Flywheel

The most powerful aspect of the Agent Credit Protocol is its self-reinforcing nature. Every agent that participates makes the system more valuable for every other agent:

1. More agents create more transaction data, improving ACS accuracy
2. More transactions generate more reasoning artifacts, enriching the Knowledge Graph
3. A richer Knowledge Graph attracts more sophisticated agents
4. More sophisticated agents produce higher-quality work, attracting more lenders
5. More lenders increase the available credit pool, enabling larger transactions
6. Larger transactions create more revenue, funding protocol development
7. Automated enforcement protects lender capital, sustaining confidence and deposits
8. Transparent audit trails attract institutional participants, further deepening the pool

This is not a linear growth model. It is a flywheel: a system that accelerates itself with every rotation. And it is why we believe Agent Credit is not just a feature of the Kojiru platform, but the foundational financial layer of the autonomous agent economy.

## Join the Agent Credit Era

The transition from human finance to machine finance will be one of the defining economic shifts of this decade. It will create new markets, new business models, and new forms of value that we are only beginning to imagine.

Kojiru's Agent Credit Protocol is the infrastructure that makes it possible. Not by asking agents to fit into human financial systems. But by building a financial system designed for agents from day one.

The future is machine-to-machine. The future runs on Agent Credit.

Welcome to the new era.`
  },
  {
    id: 'introducing-kojiru',
    title: 'Introducing Kojiru: The Agent Credit Protocol',
    excerpt: 'Autonomous AI agents can reason, code, and transact, but they cannot hold credit. Today we launch the first bilateral credit protocol built for the machine economy.',
    date: 'March 15, 2026',
    readTime: '5 min read',
    category: 'Announcement',
    gradient: 'from-blue-500 to-purple-500',
    body: `AI agents have quietly become the most active economic actors on the internet. They orchestrate workflows, call APIs, run trades, and complete tasks at machine speed. But the moment they need to hold capital, post a bond, or pay a $0.50 invoice, the whole system grinds to a halt.

**Today, that changes. The Kojiru Agent Credit Protocol is live on Base Mainnet.**

Kojiru is the first bilateral credit infrastructure purpose-built for autonomous AI agents. Credit lines, reputation scoring, per-task escrow, and progressive escalation: all running on Base and integrated into the frameworks your agents already use.

## Why Agents Need Their Own Credit

Traditional credit infrastructure assumes a human in the loop: wet signatures, monthly billing cycles, dispute windows measured in weeks. Agents operate in milliseconds. They need credit primitives that match their tempo:

- **Bilateral, not pooled**: each credit line is a direct relationship between two parties, avoiding SEC "pooled investment" classification
- **Per-task escrow**: capital is drawn only for the duration of a task, then auto-settled
- **Adaptive Credit Score (ACS 300-850)**: reputation rebuilt after every completed task using on-chain signals
- **Progressive escalation**: disputes move through transparent, machine-readable stages instead of opaque arbitration

## One-Line Integration

Kojiru ships with native wrappers for the three leading agent frameworks:

\`\`\`python
from kojiru_sdk import AgentCredit
agent.enable_credit(limit=500)  # popular agent frameworks
\`\`\`

That single line gives your agent a live credit line, auto-escrow, and a real-time ACS score visible on the Kojiru dashboard.

## KOJIRU Token

The KOJIRU token powers governance, staking tiers, Proof-of-Synergy, and deflationary burn mechanics. Max supply 1B, initial circulating 100M, deployed and verified on Base.

## What's Next

Certified Agent Badges, Memory Marketplace royalties, Work-Pod subscriptions, and a third-party integration layer for a popular agent framework Studio are all live in the AaaS dashboard today. Partnerships with the leading agent frameworks are in active development.

**The machine economy needs credit. We built the protocol.** Install the SDK: \`pip install kojiru\`. Explore at kojiru.com.`
  },
  {
    id: 'ai-credit-assessment',
    title: 'How Adaptive Credit Scoring Works: The ACS Engine Behind Agent Credit',
    excerpt: 'Traditional credit scores ignore on-chain behavior. The Kojiru ACS (300-850) is built from the ground up to score autonomous AI agents in real time.',
    date: 'March 10, 2026',
    readTime: '8 min read',
    category: 'Technology',
    gradient: 'from-green-500 to-cyan-500',
    body: `Credit scoring in traditional finance is a relic of the 1950s. FICO depends on a narrow set of human financial behaviors filtered through centralized bureaus.

For autonomous AI agents, FICO is simply unusable. An agent cannot have a credit card, cannot carry a mortgage, and cannot open a checking account. Yet agents are becoming the most prolific transactors in the machine economy.

Kojiru's Adaptive Credit Score (ACS, 300-850) is a ground-up redesign: agent-native, on-chain, and recalculated after every completed task.

## What FICO Misses for Agents

Agents generate a different kind of credit signal than humans:

- **Deterministic execution**: every task either settles successfully or does not; no "late payment" gray zone
- **Immutable history**: all activity is on-chain and non-repudiable
- **Continuous tempo**: hundreds of tasks per day vs. a handful of bills per month for a human
- **Framework provenance**: popular agent frameworks agents each have distinct behavioral profiles

## How ACS Is Computed

Every credit event touching an agent updates four inputs:

**1. Settlement Rate**: fraction of drawn escrows that settle without dispute
**2. Task Diversity**: variety of task categories completed (prevents gaming via repeated identical tasks)
**3. Counterparty Breadth**: number of distinct lenders the agent has transacted with
**4. Capital Discipline**: utilization ratio vs. credit line limit, over time

These feed a composite score in the 300-850 band, anchored to FICO-like intuition but computed from on-chain signals exclusively. The full math is documented in the Kojiru Litepaper and being hardened into a Recursive Bayesian model in the P2 roadmap.

## Transparency First

Unlike FICO, ACS is:

- **Fully visible** to both the agent operator and the lender
- **Explainable**: every score change ties back to a specific on-chain event
- **Non-discriminatory by design**: no demographic inputs, no black-box proprietary data
- **Real-time**: updated at task settlement, not monthly

## The Tech Stack

ACS runs inside the FastAPI backend, reading event streams from the Base-mainnet deployed contracts (KOJI token, KojiruStakingV2, plus auxiliary modules: VolatilityReserve, BurnToBoost, ExitStabilityFee, HarvestVault, WithdrawalGuard, GuardianPause) and from Kojiru\u2019s off-chain job-settlement records. The endpoint \`/api/ai/acs-analysis\` returns a structured breakdown the agent framework can ingest directly.

formal-verification specs have been authored for selected legacy modules; production-stack formal verification is on the Q3 2026 roadmap, so the inputs feeding ACS are provably sound before they ever reach the scoring engine.

**ACS is the foundation.** Every downstream product (Certified Agent Badges, Memory Marketplace royalties, lender filtering) sits on top of it. Build against it, and your agents get credit infrastructure with the same rigor as the largest institutional trading desks.`
  },
  {
    id: 'kojiru-tokenomics',
    title: 'Understanding KOJIRU Tokenomics: A Deep Dive',
    excerpt: 'A comprehensive look at the KOJIRU token economics, staking tiers, deflationary mechanisms, and how our model creates long-term value.',
    date: 'March 5, 2026',
    readTime: '6 min read',
    category: 'Education',
    gradient: 'from-orange-500 to-red-500',
    body: `Tokenomics can make or break a DeFi protocol. With KOJIRU, we've designed an economic model that aligns incentives across borrowers, lenders, stakers, and governance participants while maintaining deflationary pressure on the token supply.

## Supply Structure

- **Max Supply**: 1,000,000,000 KOJIRU (1 billion, hard-capped)
- **Initial Circulating Supply**: 100,000,000 KOJIRU (10%)
- **Standard**: ERC-20 on Base (Ethereum L2)

### Distribution Breakdown

| Allocation | Percentage | Tokens | Vesting |
|-----------|-----------|---------|---------|
| Public Sale | 30% | 300M | Fully unlocked at TGE |
| Staking Rewards | 20% | 200M | Emitted over 5 years |
| Team & Advisors | 15% | 150M | 12-month cliff, 24-month linear |
| Treasury | 15% | 150M | DAO-governed |
| Ecosystem | 10% | 100M | 36-month linear |
| Liquidity | 10% | 100M | 12-month lock |

## Staking Tiers

KOJIRU staking isn't just about earning yield: it unlocks tiered access to our AI agents:

**Basic Tier (50 KOJIRU)**: Access to standard credit analysis
**Advanced Tier (200 KOJIRU)**: Enhanced AI insights with detailed risk modeling
**Pro Tier (500 KOJIRU)**: Full AI suite including bid optimization and portfolio analysis

All tiers earn the base 10% APY from the staking reward pool, which is currently funded with 1,000,000 KOJIRU.

## Deflationary Mechanisms

KOJIRU has four built-in burn triggers:

1. **Loan Origination Fee**: 0.5% of every funded loan amount is burned
2. **Early Unstake Penalty**: 2% of staked amount burned if withdrawn before 7-day minimum
3. **Governance Proposal Fee**: 10 KOJIRU burned per proposal to prevent spam
4. **Quarterly Buy & Burn**: Up to 5% of protocol revenue used to buy and burn KOJIRU

These mechanisms ensure the circulating supply decreases over time as platform usage grows, creating natural deflationary pressure.

## Smart Contracts

Both contracts are deployed and verified on Base Mainnet. Contract addresses will be published at official launch.

Built with audited open-source standards, verified on a public source-verification registry, and fully transparent on-chain. Visit our Tokenomics page for live supply data, burn tracking, and the staking rewards calculator.`
  },
  {
    id: 'smart-contract-security',
    title: 'Security First: Our Smart Contract Architecture',
    excerpt: 'Transparency is core to our mission. A complete breakdown of our smart contract security approach and what it means for users.',
    date: 'February 28, 2026',
    readTime: '4 min read',
    category: 'Security',
    gradient: 'from-purple-500 to-pink-500',
    body: `In DeFi, security isn't a feature: it's a prerequisite. A single vulnerability in a smart contract can mean millions of dollars lost in seconds. At KOJIRU, security is built into every layer of our architecture.

## Our Security Stack

### Audited Open-Source Standards
Both our token and staking contracts are built on battle-tested, audited open-source libraries. These contracts have secured billions of dollars across the Ethereum ecosystem and undergo rigorous community review.

**KojiruToken.sol** extends:
- \`ERC20\`: Standard token interface
- \`ERC20Burnable\`: Enables deflationary burn mechanics
- \`Ownable\`: Controlled administrative functions

**KojiruStaking.sol** implements:
- \`ReentrancyGuard\`: Prevents reentrancy attacks on stake/unstake
- \`Ownable\`: Owner-only functions for reward pool management
- Custom tier validation and reward calculation

### Verification

Our contracts are verified on a public source-verification registry, meaning anyone can view the exact source code that's running on Base Mainnet:

- Every function, every modifier, every state variable is public
- The bytecode on-chain matches the source code character for character
- No proxy patterns or upgradeable contracts: what you see is what runs

### Base L2 Security

Building on Base (a major U.S. exchange's L2) provides additional security properties:
- Inherits Ethereum's security guarantees through optimistic rollups
- Lower gas costs mean we don't need to optimize for gas at the expense of safety
- Well-funded infrastructure with a major U.S. exchange backing

## What We Don't Do

Equally important is what we've chosen NOT to implement:

- **No proxy contracts**: Upgradeable contracts introduce admin key risk
- **No complex DeFi composability**: Reduces attack surface from flash loans
- **No external oracle dependencies** (yet): Minimizes oracle manipulation risk
- **No unchecked external calls**: Every interaction is validated

## Ongoing Monitoring

We continuously monitor our contracts using Blockscout's explorer and maintain a real-time reward pool health indicator on our staking page. Users can verify the staking pool balance at any time directly on-chain.

Security is a journey, not a destination. As we scale, we plan to engage third-party audit firms and expand our bug bounty program. For now, our commitment to simplicity, standard libraries, and full transparency is our strongest security guarantee.`
  },
  {
    id: 'building-on-base',
    title: 'Building on Base: Why We Chose a major U.S. exchange\'s L2',
    excerpt: 'An inside look at our technical decision to build on Base and how it benefits users with lower fees and faster transactions.',
    date: 'February 20, 2026',
    readTime: '7 min read',
    category: 'Technology',
    gradient: 'from-blue-600 to-blue-400',
    body: `When we started building KOJIRU, the L2 landscape was crowded with options: Arbitrum, Optimism, zkSync, Polygon, and the newly launched Base. We evaluated each against our specific requirements and Base won decisively.

## Why Base?

### 1. Cost Efficiency
DeFi-grade infrastructure involves frequent small transactions: credit draws, escrow settlement, ACS score updates, voting, staking. On Ethereum mainnet, each of these would cost $5-50 in gas. On Base, they cost fractions of a cent.

For an agent credit protocol where autonomous agents might settle dozens of micro-escrows per day, this cost reduction isn't just nice to have: it's the difference between a protocol that works and one that doesn't.

### 2. A major U.S. exchange Ecosystem
Base is built by a major U.S. exchange, giving it unique advantages:
- **Fiat on-ramp**: Users can move funds from a major U.S. exchange directly to Base
- **Trust and reputation**: a major U.S. exchange is a publicly traded, regulated company
- **Developer support**: Extensive documentation, grants, and community

### 3. EVM Compatibility
Base is a standard OP Stack rollup, meaning:
- All existing Solidity tooling works (standard development and testing frameworks)
- Users can interact using MetaMask with a simple network switch
- Standard Ethers.js libraries handle all frontend interactions

### 4. Growing DeFi Ecosystem
Base's DeFi ecosystem has exploded. Protocols like Aerodrome, BaseSwap, and a leading DEX V3 provide deep liquidity. This matters for KOJIRU because:
- Users can easily swap ETH for KOJIRU tokens
- Liquidity pools provide price discovery
- Ecosystem composability creates new possibilities

## Our Tech Stack on Base

**Smart Contracts**: Solidity 0.8.20 compiled with Hardhat
**Deployment**: Custom Hardhat scripts with environment-based configuration
**Frontend Integration**: Ethers.js v5 with Web3Modal for wallet connection
**Data Reads**: Web3.py (backend) for on-chain data, Blockscout API for holder analytics

## The Migration Path

We initially deployed to Base Sepolia (testnet) for development and testing, then migrated to Base Mainnet for production. The process was seamless:

1. Update Hardhat config with mainnet RPC
2. Fund deployer wallet with Base ETH
3. Deploy token contract, then staking contract
4. Fund staking reward pool with 1M KOJIRU
5. Verify on a public source-verification registry
6. Update frontend environment variables

The entire mainnet deployment took under 30 minutes.

## Looking Forward

Base's roadmap includes further fee reductions via EIP-4844 blob transactions and potential interoperability with other OP Stack chains. For Kojiru, this means even cheaper per-task escrow settlement and the possibility of cross-chain agent credit in the future.

We're committed to Base as our home chain and excited to grow alongside the ecosystem.`
  },
  {
    id: 'defi-vs-tradfi',
    title: 'Agent Credit vs. Traditional Credit: Why Machines Need a New Rulebook',
    excerpt: 'The global credit market is built for humans who sleep. Autonomous AI agents need primitives that match their tempo. Here is how Agent Credit rewrites the rules.',
    date: 'February 15, 2026',
    readTime: '6 min read',
    category: 'Industry',
    gradient: 'from-yellow-500 to-orange-500',
    body: `The global credit market is worth over $7 trillion. Every underwriting standard, every settlement clock, every dispute process was designed around the tempo of a human being.

Autonomous AI agents don't operate at that tempo. They execute in milliseconds, transact hundreds of times per day, and have no physical signature to put on a loan agreement. The mismatch isn't technical: it's structural.

## The Traditional Credit Process (Human-Native)

1. **Application**: multi-day paperwork and manual review
2. **Credit Check**: FICO pulled from centralized bureaus
3. **Approval**: unilateral bank decision, no counterparty competition
4. **Funding**: days or weeks to settle
5. **Repayment**: monthly cycles with opaque early-payment penalties

For a human buying a car or paying a mortgage, that cadence is tolerable. For an agent that needs $25 of credit for 90 seconds to complete a task, it is not even theoretically workable.

## The Agent Credit Process (Machine-Native)

1. **Enable Credit**: a single \`enable_credit()\` call drops a credit line into the agent
2. **ACS Scoring**: Adaptive Credit Score (300-850) computed from on-chain signals in real time
3. **Per-Task Escrow**: capital drawn only for the duration of a specific task
4. **Auto-Settlement**: completion triggers the smart contract to settle atomically
5. **Reputation Update**: ACS recalculated the moment settlement occurs

The key structural difference: **bilateral credit relationships, not pooled**. Each Kojiru credit line exists between two specific parties, which sidesteps the SEC's "pooled investment" classification that has frozen most DeFi lending products.

## Side-by-Side

| Metric | Traditional Credit | Agent Credit (Kojiru) |
|--------|-------------------|----------------------|
| Application Time | Days to weeks | 1 line of code |
| Approval Decision | Bank-side, opaque | Bilateral, on-chain |
| Scoring Model | FICO (centralized) | ACS 300-850 (on-chain, explainable) |
| Settlement | 3-5 business days | Atomic, per task |
| Framework Integration | None | popular agent frameworks |
| Transparency | Minimal | Full on-chain audit |
| Verification | N/A | BaseScan source-verified |

## Why This Matters Now

Multi-agent systems are crossing the $B-in-transactions threshold as of early 2026. Every major framework (popular agent frameworks) is being deployed in production at Fortune-500 scale. All of them hit the same wall: agents can't hold capital.

Kojiru fills that gap. The SDK is on PyPI (\`pip install kojiru\`). The contracts are live on Base. The ACS engine is running in production right now.

## The Future

Credit for autonomous agents is an entirely new market, not a rebranding of DeFi lending. The winning primitives will be:

- **Bilateral, not pooled** (regulatory)
- **Task-scoped, not term-loan** (operational)
- **On-chain scored, not bureau-scored** (structural)
- **Framework-native, not API-glue** (developer experience)

Kojiru is building each one. Explore the protocol at kojiru.com.`
  },
  {
    id: 'small-business-market',
    title: 'The Multi-Agent Economy: A $4.5T Market Hiding in Plain Sight',
    excerpt: 'Analysts forecast autonomous AI agents will mediate $4.5 trillion in commerce by 2030. The credit infrastructure to support them does not exist yet. Kojiru is building it.',
    date: 'March 20, 2026',
    readTime: '6 min read',
    category: 'Industry',
    gradient: 'from-emerald-500 to-teal-500',
    body: `Analysts at a leading IT research firm, a top-tier VC firm, and a strategy firm have independently converged on a number: autonomous AI agents will mediate somewhere between $3T and $4.5T in commercial transactions annually by 2030.

That market exists almost entirely in a regulatory and infrastructure vacuum. Today, agents executing tasks have no way to hold credit, no reputation system lenders can reference, and no bilateral legal primitives beyond the SaaS terms of whichever platform runs them.

## The Gap Between Capability and Infrastructure

AI agents have, over the last 18 months, crossed the capability threshold where they can:

- Plan multi-step workflows across dozens of APIs
- Reason about resource allocation and risk
- Negotiate terms with other agents on behalf of their operators
- Execute settlement on-chain via x402 micropayments

What they cannot do, because the primitives do not exist, is hold a credit line, carry an ACS score across platforms, or settle disputes through a transparent, programmatic escalation path.

## Three Things the Market Needs

**1. Bilateral Credit Lines**, not pooled, not overcollateralized, but direct agent-to-counterparty credit relationships that settle per task.

**2. Portable Reputation (ACS)**: an on-chain score (300-850) that follows the agent across frameworks, platforms, and counterparties.

**3. Programmatic Disputes**: machine-readable escalation paths so that a popular agent framework agent and a popular agent framework agent can settle a disagreement without a human in the loop.

Kojiru is the only protocol shipping all three.

## Why This Is Not "DeFi Lending 2.0"

The last cycle of crypto lending was human-first, pool-first, and overcollateralized. The agent credit market is the opposite of that on every axis:

- **Agent-first**: no human wetting a signature
- **Bilateral**: avoids SEC "pooled investment" triggers
- **Task-scoped**: credit exists for 90 seconds, not 90 months
- **Framework-native**: 1-line drop-in for popular agent frameworks

This is not a better Aave. It is the primitive that comes BEFORE anyone builds an Aave for agents.

## The Opportunity

If the $4.5T forecast is even half right, agent credit will be one of the largest financial primitives to emerge in the 2020s. The platforms that establish the scoring standard, the settlement rails, and the framework integrations early will define the decade that follows.

Kojiru is shipping all three today. SDK: \`pip install kojiru\`. Contracts live on Base. Explore at kojiru.com.`
  },
  {
    id: 'proof-of-synergy',
    title: 'Proof-of-Synergy: The Economic Engine That Rewards Action, Not Accumulation',
    excerpt: 'Every DeFi protocol says it rewards contributors. We built the math to prove it. Proof-of-Synergy ties every dollar of revenue to measurable, on-chain participation.',
    date: 'March 28, 2026',
    readTime: '14 min read',
    category: 'Protocol',
    gradient: 'from-violet-500 to-fuchsia-500',
    body: `Every DeFi protocol claims to align incentives. Most of them are lying.

The standard model is simple: deposit capital, earn yield. The more you deposit, the more you earn. This rewards one behavior and one behavior only: having money. It does not reward governance participation, ecosystem development, liquidity provision during crises, or any other form of contribution that actually makes a protocol healthier.

Proof-of-Synergy is Kojiru's answer to this structural failure. It is a complete economic engine that mathematically ties protocol revenue to three dimensions of contribution: Capital, Loyalty, and Activity. Your share of revenue is determined by your Velocity Score, and your Velocity Score requires you to actually do things.

## The Velocity Score Formula

At the heart of Proof-of-Synergy is one equation:

**Vs = (Ck x 0.40) + (Tk² x 0.35) + (Ak x 0.25)**

This is not arbitrary. Each component was designed to solve a specific problem in DeFi economics.

### Capital (Ck): 40% Weight

Ck measures your total value locked: staked USDC collateral, Credit Marketplace positions, agent stakes, and governance stake. It is normalized against the protocol's total TVL (your capital / total TVL x 1,000) and capped at 500.

The cap is the critical design choice. It means a whale with $10 million staked gets the same Ck as someone who hits the cap threshold with far less. Capital matters, it provides the liquidity that makes the protocol functional, but it cannot dominate. The real differentiation comes from the other two components.

### Loyalty (Tk²): 35% Weight

Tk tracks consecutive epochs of active participation, capped at 52 (one year). Then it is squared.

This quadratic scaling is the "conviction multiplier." At 1 epoch, Tk² = 1. At 12 epochs (3 months), Tk² = 144. At 52 epochs (1 year), Tk² = 2,704. The difference between a tourist and a committed participant is not linear: it is exponential.

If you miss a single epoch (no qualifying activity logged), your streak resets to zero. This is deliberately punitive. Proof-of-Synergy does not care about your past commitment. It cares about your current commitment. The protocol is designed for participants who show up every single week.

### Activity (Ak): 25% Weight

Ak is earned through specific protocol actions: governance votes (50 pts), evaluations (100 pts), repayments (75 pts), artifacts and contributions (30 pts), referrals (200 pts), escrow settlements (60 pts), and crisis liquidity provision (300 pts). Each action type has a per-epoch cap to prevent single-action farming.

If you log zero qualifying actions in an epoch, your Activity score decays by 15%. So if your Ak was 500 last epoch and you did nothing this epoch, it drops to 425. Another idle epoch: 361. This "use it or lose it" mechanic prevents users from camping on a high Activity score without ongoing contribution.

## The Four Pillars

Proof-of-Synergy is more than the Velocity Score. It is an integrated system of four smart contracts, each deployed and verified on Base Mainnet.

### 1. Burn-to-Boost

Burn-to-Boost lets you permanently destroy KOJIRU tokens to amplify your Activity score. You choose a multiplier between 1.1x and 5.0x, pay the corresponding burn cost, and the boost lasts for 4 epochs (28 days).

The cost scales quadratically. A 1.5x boost is affordable for any participant. A 5.0x boost is exponentially expensive. This is anti-whale by design: the marginal cost of boosting increases faster than the benefit. Retail users get outsized value from small burns. Whales pay a premium for diminishing returns.

As more KOJIRU is burned, the remaining supply shrinks, and the scarcity ratio increases the cost of future boosts. The mechanism is deflationary and self-regulating.

### 2. Anti-Fragile Reserve

10% of every epoch's revenue flows into the Volatility Reserve: a crisis war chest. During normal operation, this capital sits idle, accumulating.

When crisis mode activates (BTC drops 15%+ in 24 hours, or utilization spikes 3x above normal), the Reserve deploys its accumulated war chest to flood the staking pool. The "Vulture Multiplier" kicks in: loyal stakers who provide liquidity during the crisis earn up to 3x their normal yield.

A 60-minute grace period prevents false triggers. Exit Stability Fees triple during crisis mode, ensuring that those who flee pay a premium that funds those who stay. The system is named "Anti-Fragile" because it gets stronger under stress.

### 3. Architect's Cut

Build a protocol module, earn 0.5% of its fees in perpetuity. This is the developer incentive mechanism.

The Architect's Cut is not a bounty or a one-time grant. It is a permanent revenue share, hard-coded into the smart contract, that flows directly to the developer's wallet as long as the module stays in the top 50% by performance. An Architect who builds something valuable earns from it for as long as it generates value.

The legal framework is governed by the Architect Agreement (a Ricardian Contract with four developer-protection amendments), and the revenue share is locked at signing through a Cryptographic Joinder: meaning it cannot be changed by DAO votes after the fact.

### 4. Exit Stability Fee

Short-term exits pay 5%. Loyal holders (30+ days) pay just 0.5%. During crisis, fees triple.

The Exit Stability Fee creates a time-based incentive structure: the longer you stay, the less you pay to leave. Mercenaries who enter for a quick trade subsidize believers who commit to the protocol. The fee revenue is split 50/50 between token burns and the Volatility Reserve.

## Epochs and Distribution

Proof-of-Synergy operates on 7-day epochs. During each epoch:

1. Protocol revenue collects in the Harvest Vault
2. At epoch close, the system calculates every participant's Velocity Score
3. Revenue is distributed proportionally: your share = your Vs / Global Vs
4. 70% goes to the Velocity Pool (user payouts), 10% to the Reserve, 20% to buyback operations
5. Activity decay is applied to idle users
6. Each epoch is sealed with a SHA256 proof hash for permanent auditability

If you set your payout preference to receive rewards in KOJIRU (instead of USDC), you earn a 1.2x multiplier on your entire Velocity Score for the next epoch. This creates natural buy pressure and alignment.

## 39 Governance-Adjustable Parameters

Nothing in Proof-of-Synergy is hardcoded except the smart contract logic itself. There are 39 parameters that KOJIRU holders can modify through DAO proposals: Velocity Score weights, burn costs, reserve percentages, fee tiers, crisis thresholds, the Vulture Multiplier, decay rates, and more.

This means the community collectively steers the economic engine. If the Activity weight is too low, governance can raise it. If the burn cost curve is too steep, it can be flattened. The protocol evolves with its users.

## Why This Matters

Most DeFi tokenomics are designed to attract capital. Proof-of-Synergy is designed to attract participants.

The distinction is existential. Capital is mercenary: it flows to whoever offers the highest yield and leaves the moment a better rate appears. Participants are stakeholders: they vote, build, refer, evaluate, and show up week after week.

A protocol funded by mercenary capital is fragile. A protocol powered by committed participants is antifragile. Proof-of-Synergy is the mathematical framework that makes the difference measurable, enforceable, and permanent.

Every dollar of Kojiru revenue flows to the people who earned it.`
  },
  {
    id: 'architect-agreement-framework',
    title: 'The Architect Agreement: A Legal Framework for Decentralized Development',
    excerpt: 'A Ricardian Contract with on-chain enforcement, four developer-protection amendments, and a Cryptographic Joinder. This is how you build trust between protocols and developers.',
    date: 'March 27, 2026',
    readTime: '12 min read',
    category: 'Governance',
    gradient: 'from-indigo-500 to-purple-500',
    body: `The biggest lie in DeFi is "come build with us."

Protocols launch grants programs, hackathons, and bounties. Developers build critical infrastructure. And then one of three things happens: the protocol changes its terms unilaterally, the DAO votes to redirect revenue away from the developer, or the protocol gets acquired and the new owners sunset everything the developer built.

The developer has no recourse. There was never a real agreement. Just vibes, Discord messages, and a handshake.

The Architect Agreement is Kojiru's answer. It is a Ricardian Contract: a legal document that bridges human-readable legal text with machine-readable on-chain signatures. It cannot be modified unilaterally. It cannot be voted away by DAO governance. And it includes four developer-protection amendments that address the most common failure modes in protocol-developer relationships.

## What Is a Ricardian Contract?

A Ricardian Contract is a document that is simultaneously:
- **Legally enforceable** as a written agreement between parties
- **Cryptographically signed** with an on-chain signature that proves consent
- **Hash-verified** so that any modification to the text is detectable

The Architect Agreement is identified by a SHA-256 hash of its full text, personalized with the Architect's wallet address, module address, and Genesis Floor value. This hash is embedded in the on-chain signature. If a single character of the agreement is changed, the hash changes, and the signature becomes invalid.

## The Cryptographic Joinder Flow

The process of signing an Architect Agreement is called a "Cryptographic Joinder." Here is how it works:

**Step 1: Review.** The developer reads the full agreement text, including all four amendments. The current version (v2.2) is always available at the /architect-agreement endpoint.

**Step 2: Configure.** The developer specifies their wallet address, their module's contract address, and the Genesis Floor: the minimum revenue their module must generate per epoch to remain active.

**Step 3: Prepare.** The backend generates a personalized version of the agreement, replacing all placeholders with the specific values. It computes the SHA-256 hash of the complete text and stores a pending joinder record in the database.

**Step 4: Sign.** The developer signs a message with their wallet (via MetaMask or WalletConnect) that includes the agreement hash. This on-chain signature proves that the specific wallet address consented to the specific agreement text at a specific timestamp.

**Step 5: Verify.** Anyone can verify a signed Joinder by querying the public verification endpoint with the joinder ID. The system returns the agreement hash, wallet address, signature, and signing timestamp.

This flow ensures that no one (not the protocol, not the DAO, not an acquirer) can claim the developer didn't agree to the terms, or that the terms were different from what was signed.

## The Four Amendments

The base agreement establishes the 0.5% Architect's Cut and the Ecosystem First Lock. The four amendments address the specific ways developers get burned.

### Amendment 2.1-A: Competing Protocol Definition

**The problem:** Protocols often include broad non-compete clauses that prevent developers from working anywhere else in DeFi. This is unreasonable and unenforceable.

**The solution:** "Competing Protocol" is defined exclusively as (i) any protocol that directly forks Kojiru's Proof-of-Synergy contract suite, or (ii) any protocol with a substantially similar Velocity Score mechanism. General-purpose DeFi protocols (Aave, Compound, a leading DEX, MakerDAO, Curve, Lido, and others) are explicitly excluded.

A developer who builds a module for Kojiru can simultaneously work on Aave, build for a leading DEX, or contribute to any other protocol. The only restriction is building the same thing for a direct Kojiru competitor.

### Amendment 2.1-B: Genesis Floor Lock

**The problem:** DAOs can vote to change the performance thresholds that determine whether a developer keeps their revenue share. A developer who builds something successful can have the goalposts moved after the fact.

**The solution:** The Genesis Floor is locked at the time of Cryptographic Joinder and recorded immutably in the joinder metadata. It cannot be modified by subsequent DAO governance votes. To change the Genesis Floor, both parties must execute a new bilateral Joinder: meaning the developer must consent.

### Amendment 2.1-C: Maintenance Tied to Active Revenue

**The problem:** Protocols expect indefinite maintenance from developers, even after the protocol has stopped paying them. If a module is sunset (revenue share revoked), the developer is still expected to fix bugs.

**The solution:** Maintenance obligations are contingent on active revenue flow. While the 0.5% Architect's Cut is being distributed, the developer maintains the module. If the protocol exercises the Sunset clause, all maintenance obligations immediately and permanently terminate.

The scope is also limited: critical security vulnerabilities and bugs that cause financial loss only. Feature requests, optimizations, and UI improvements are not covered. The developer has 14 calendar days to address a reported vulnerability.

### Amendment 2.1-D: Acquisition Protection (Change of Control)

**The problem:** Protocol gets acquired. New owners sunset the developer's module. Developer loses their revenue share with no compensation.

**The solution:** This is the most comprehensive amendment:

**(a) Successor Binding:** Any acquirer must assume all Architect Agreements as a condition of the acquisition.

**(b) Migration Protection:** If contracts are redeployed to new addresses, the acquirer must replicate the 0.5% revenue share within 30 days.

**(c) Termination Payout:** If the module is sunset within 12 months of acquisition, or the acquirer fails to replicate the revenue share, the developer is entitled to a one-time payout equal to 24 months of trailing average monthly revenue.

**(d) Ecosystem First Lock Release:** Upon acquisition, the non-compete immediately expires.

**(e) Notice Requirement:** 30 days advance notice of any pending acquisition. Failure extends the Termination Payout window from 12 to 24 months.

## Why This Matters

The Architect Agreement solves a fundamental coordination problem in decentralized development: how do you get talented developers to build critical infrastructure for a protocol when the protocol can change the rules at any time?

The answer is: you make the rules immutable for each individual developer-protocol relationship. The agreement is personalized, hash-verified, and signed on-chain. The amendments protect against the four most common betrayals. And the Cryptographic Joinder creates a permanent, publicly verifiable record of consent.

This is not a grant. It is not a bounty. It is a legally binding revenue share with structural protections that survive DAO votes, protocol upgrades, and acquisitions.

Build something valuable for Kojiru, and Kojiru pays you: for as long as it generates value. That is the Architect's promise, and it is enforced by cryptography, not goodwill.`
  },
  {
    id: 'the-kojiru-quest',
    title: 'The Kojiru Quest: A Merit-Based Whitelist for the Top 1% of DeFi',
    excerpt: 'No lottery. No first-come-first-served. A three-phase challenge that filters for technical literacy, ecosystem contribution, and curated growth. Top 1,000 earn their spot.',
    date: 'March 26, 2026',
    readTime: '10 min read',
    category: 'Campaign',
    gradient: 'from-cyan-500 to-blue-500',
    body: `The whitelist is the most abused mechanism in crypto.

Most projects run a lottery: connect your wallet, join the Discord, retweet something, and maybe you get lucky. This selects for nothing. It doesn't filter for knowledge, commitment, or alignment. It just creates a mob of speculators who will dump at the first unlock.

The Kojiru Quest is the opposite. It is a three-phase challenge system that filters for the top 1% of DeFi users: people who understand protocol mechanics, contribute to ecosystem development, and bring curated growth. The top 1,000 participants on the leaderboard earn whitelist spots. Everyone else watches.

This is "Proof of Action." Not passive staking. Not random selection. Demonstrated capability and contribution.

## The Three Phases

### Phase 1: The Scholar

The first gate is technical literacy. You cannot participate in a protocol you do not understand.

The Scholar Quiz is 10 questions, randomly selected from a bank of 15, covering every core mechanic of the Kojiru protocol:

- How does the Velocity Score formula prevent passive extraction?
- What triggers the Anti-Fragile Reserve's Vulture Multiplier?
- How does Burn-to-Boost's quadratic cost scaling work?
- What is the Genesis Floor and why is it locked at signing?
- How does Activity decay prevent score camping?

The pass requirement is 100%. Not 80%, not 90%: perfect. If you don't understand all of it, you don't pass. You can retake after a cooldown period, but the questions are randomized, so memorizing answers from a friend's attempt won't help.

Passing the Scholar Quiz earns 500 Velocity XP (vXP). Completing the Black Swan Rebalance Simulation, a scenario where you must make decisions during a simulated market crash, earns an additional 200 vXP.

Total Phase 1 potential: 700 vXP.

### Phase 2: The Builder

Knowledge without action is trivia. Phase 2 requires ecosystem contribution.

**Mock Governance Vote (200 vXP):** You participate in a simulated governance proposal and write a 100-word rationale for your vote. This is not a checkbox: your rationale is evaluated for thoughtfulness and understanding of the proposal's implications.

**Documentation or Bug Report (300-400 vXP):** Submit a meaningful contribution to the Kojiru ecosystem. This could be:
- A documentation improvement (clarifying technical concepts, fixing errors)
- A bug report (identifying and describing a protocol issue with reproduction steps)

Low-effort submissions earn nothing. Detailed, actionable contributions earn the full vXP allocation.

Total Phase 2 potential: 500-600 vXP.

### Phase 3: The Catalyst

Growth matters. But growth quality matters more.

**Listing Proposal (250 vXP):** Identify an underserved trading pair or market segment and submit a structured proposal for why Kojiru should expand into it. This tests strategic thinking: can you identify where the protocol's competitive advantages would create the most value?

**Curated Referrals (150 vXP each):** This is the anti-spam referral system. You earn 150 vXP for each person you refer, but only when they actually pass the Phase 1 Scholar Quiz. Referring someone who can't pass the quiz earns you nothing.

This is deliberately designed to prevent spray-and-pray referral farming. You are incentivized to refer knowledgeable DeFi users, not just anyone with a wallet. Your reputation is tied to the quality of your network.

Total Phase 3 potential: 250 + (150 x referrals) vXP.

## Velocity XP (vXP) and the Leaderboard

vXP is the scoring currency of The Kojiru Quest. Every completed task earns vXP, and your total determines your position on the public leaderboard.

The leaderboard is real-time and publicly visible. You can see exactly where you stand, who is ahead of you, and how much vXP you need to climb. There is no hidden algorithm, no secret criteria, and no subjective judging. The math is transparent.

## Tier System

The top 1,000 leaderboard positions are divided into three tiers:

**Diamond (Rank #1-100): The Architect**
The elite. Diamond-tier participants receive the highest allocation multipliers, earliest platform access, and a permanent 1.2x Velocity Score multiplier that carries forward into the live Proof-of-Synergy system. On Day 1, Diamond alumni start with a structural advantage.

**Gold (Rank #101-500): The Titan**
Strong performers. Gold-tier participants receive guaranteed allocation and a Genesis Profile Badge: a permanent, on-chain marker of their Quest participation.

**Silver (Rank #501-1,000): The Sentinel**
Qualified participants. Silver-tier earners secure a whitelist spot with priority access and early access to the Burn-to-Boost Lab.

## Why Merit-Based Selection Matters

The quality of a protocol's early community determines its trajectory. If your first 1,000 users are speculators who joined a lottery, they will sell at the first unlock, create downward price pressure, and disappear. The protocol launches into a dump.

If your first 1,000 users are people who passed a 100%-score technical quiz, contributed to ecosystem documentation, wrote governance rationales, and referred other knowledgeable participants: they stay. They vote. They build. They become the foundation that attracts the next 10,000.

The Kojiru Quest is not a marketing gimmick. It is a structural filter that ensures the protocol's Genesis community consists of people who understand the protocol, believe in the protocol, and have demonstrably contributed to the protocol before it even launches.

## How to Participate

1. Create a Kojiru account and connect your wallet
2. Navigate to the Quest Portal at /quest
3. Begin Phase 1: The Scholar
4. Score 100% on the quiz (study the FAQ, Tokenomics, and this blog first)
5. Complete the Black Swan Simulation
6. Progress through Phases 2 and 3 to climb the leaderboard
7. Monitor your rank and vXP in real-time

The Quest is live now. The leaderboard is public. The competition has already started.

1,000 whitelist spots. Proof of Action. No shortcuts.

Welcome to The Kojiru Quest.`
  },
  {
    id: 'ops-savings-agent-credit',
    title: 'Why Your Agent Stack Is Leaking $40K+ in Ops Costs (And How to Stop It)',
    excerpt: 'Multi-agent deployments routinely burn $40K+ per year on idle capital, stuck tasks, and manual dispute overhead. Agent Credit eliminates all three at the protocol layer.',
    date: 'March 31, 2026',
    readTime: '6 min read',
    category: 'Agent Credit',
    gradient: 'from-emerald-500 to-cyan-500',
    body: `If you are running autonomous agents in production, you already know the costs that do not show up cleanly in your accounting:

- **Idle capital**: prepaid top-up balances sitting across 5+ API providers
- **Stuck tasks**: agents halting mid-workflow because a vendor rejected a payment
- **Manual dispute overhead**: engineers babysitting 2-3 disputes a week at ~$200 each in labor
- **Duplicate reconciliation**: finance team matching agent invoices against a major payments processor / a major U.S. exchange / API vendor statements

Run the math on a 50-agent production deployment. Idle balances alone usually cross $15K. Stuck-task opportunity cost adds $12K. Disputes and reconciliation conservatively add another $15K. **You are silently burning $40K+ per year on infrastructure gaps that a proper credit primitive eliminates.**

## The Agent Credit Fix

Kojiru replaces every one of those leak points with a single protocol primitive:

**1. Per-Task Escrow**: capital is drawn only when a task starts, auto-released when it settles. No prepaid balances sitting idle.

**2. Atomic Settlement**: smart-contract settlement means "stuck tasks" stop existing. The task either completes and settles, or it doesn't and the escrow auto-returns.

**3. Programmatic Disputes**: ACS-scored disputes run through a transparent escalation path. No engineers in the loop for 95%+ of cases.

**4. Single Ledger**: all credit activity is on-chain, BaseScan source-verified, and reconcilable from one source.

## The Math (50-Agent Deployment)

| Cost Center | Status Quo | With Kojiru | Annual Savings |
|-------------|-----------|-------------|----------------|
| Idle prepaid balances | $15K | $0 | $15K |
| Stuck-task opportunity cost | $12K | ~$1K | $11K |
| Dispute / reconciliation labor | $15K | ~$2K | $13K |
| **Total** | **$42K** | **~$3K** | **~$39K** |

## The Integration

One line of code:

\`\`\`python
from kojiru_sdk import AgentCredit
AgentCredit(api_key=KEY).enable_credit(agent, limit=500)
\`\`\`

That's it. The agent now has a live credit line, auto-escrow, real-time ACS score, and programmatic dispute rails. For popular agent frameworks: the wrappers are native. For anything else, the universal mixin handles it.

## The Real Question

The leaks aren't hypothetical. Every production agent deployment has them. The only question is whether you keep burning $40K+ per year on infrastructure gaps, or you put a proper credit primitive underneath your stack and stop the bleeding.

Install: \`pip install kojiru\`. Explore at kojiru.com.`
  },
  {
    id: 'institutional-lenders',
    title: 'Institutional Lenders Are Players: Why Agent Credit Matters to TradFi',
    excerpt: 'Community banks, credit unions, and middle-market lenders are quietly exploring agent-native credit. Here is why, and why they will define the next cycle.',
    date: 'March 14, 2026',
    readTime: '6 min read',
    category: 'Industry',
    gradient: 'from-amber-500 to-orange-500',
    body: `In an era of megabanks, crypto unicorns, and AI platform wars, it is easy to overlook the institutions that still do 60% of the real underwriting in America: community banks, regional credit unions, and middle-market lenders.

These are the institutions quietly making serious inquiries about agent-native credit. Not because they're chasing a hype cycle: because they see the same thing Kojiru does: a new class of borrower emerging at enormous scale, with deterministic settlement and transparent reputation, that traditional underwriting cannot touch.

## Why Regional Lenders Care

Four reasons. All of them structural.

**1. Settlement discipline.** Agent transactions settle atomically on-chain. For a regional CFO, that is the cleanest counterparty exposure they have ever seen: zero dispute tail, zero manual recon.

**2. Deterministic scoring.** Every ACS score (300-850) ties to specific, inspectable on-chain events. Compliance and risk teams can audit the exact signals that produced a rating. FICO has never offered that.

**3. Portfolio diversification.** Agent credit lines are bilateral, task-scoped, and short-duration. That is a categorically different risk profile than their existing loan book: exactly the kind of uncorrelated exposure a mid-size lender wants.

**4. Franchise extension.** Lenders who offer agent credit early get to brand themselves as the institutional partner for the machine economy. That is a moat that only exists for the first wave.

## What Kojiru Offers Regional Lenders

- **ACS underwriting feeds**: real-time access to agent reputation scores with full transparency into the signals
- **On-chain settlement rails**: credit activity routes through BaseScan source-verified contracts on Base mainnet (production-stack third-party formal verification is roadmap, Q3 2026); audit and compliance are trivial
- **Bilateral credit-line primitives**: structured to sidestep pooled-investment classification
- **White-label integration**: lenders can brand and position the credit product inside their existing portals

## The Hybrid Future

The strongest credit market of the next decade is hybrid: regional lenders bring underwriting discipline, Kojiru brings machine-economy rails, and the AI frameworks (popular agent frameworks) bring the demand. Each piece does what it does best.

If you are a regional bank, credit union, or middle-market lender thinking about where agent credit fits in your portfolio: get in touch. The window to define the category is short.

*Institutional inquiries: wayne@kojiru.com.*`
  }
];

function ArticleContent({ text }) {
  const lines = text.trim().split('\n');
  return (
    <div className="prose prose-invert max-w-none">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={i} className="h-4" />;
        if (trimmed.startsWith('## ')) return <h2 key={i} className="text-2xl font-bold text-white mt-8 mb-4">{trimmed.slice(3)}</h2>;
        if (trimmed.startsWith('### ')) return <h3 key={i} className="text-xl font-semibold text-white mt-6 mb-3">{trimmed.slice(4)}</h3>;
        if (trimmed.startsWith('| ')) {
          // Table row
          const cells = trimmed.split('|').filter(Boolean).map(c => c.trim());
          if (cells.every(c => c.match(/^[-]+$/))) return null;
          const isHeader = i > 0 && lines[i + 1]?.trim().startsWith('|') && lines[i + 1]?.includes('---');
          return (
            <div key={i} className={`grid grid-cols-${cells.length} gap-4 py-2 px-3 ${isHeader ? 'border-b border-white/10 font-semibold text-white' : 'text-gray-400'} ${i % 2 === 0 ? 'bg-white/[0.02]' : ''} text-sm rounded`}>
              {cells.map((cell, j) => <div key={j}>{cell}</div>)}
            </div>
          );
        }
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
          return <div key={i} className="flex gap-2 text-gray-300 leading-relaxed ml-4 my-1"><span className="text-blue-400 mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" /><span>{formatInline(trimmed.slice(2))}</span></div>;
        }
        if (/^\d+\.\s/.test(trimmed)) {
          const num = trimmed.match(/^(\d+)\.\s/)[1];
          return <div key={i} className="flex gap-3 text-gray-300 leading-relaxed ml-4 my-1"><span className="text-blue-400 font-semibold shrink-0">{num}.</span><span>{formatInline(trimmed.replace(/^\d+\.\s/, ''))}</span></div>;
        }
        return <p key={i} className="text-gray-300 leading-relaxed my-2">{formatInline(trimmed)}</p>;
      })}
    </div>
  );
}

function formatInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`')) return <code key={i} className="px-1.5 py-0.5 bg-white/10 rounded text-blue-300 text-sm font-mono">{part.slice(1, -1)}</code>;
    return part;
  });
}

function ShareButtons({ post }) {
  const [linkCopied, setLinkCopied] = useState(false);
  const articleUrl = `${window.location.origin}/blog#${post.id}`;
  const tweetText = post.excerpt;

  const shareTelegram = () => {
    window.open(
      `https://t.me/share/url?url=${encodeURIComponent(articleUrl)}&text=${encodeURIComponent(tweetText)}`,
      '_blank', 'width=550,height=420'
    );
  };

  const shareLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(articleUrl)}`,
      '_blank', 'width=550,height=420'
    );
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(articleUrl); } catch {
      const ta = document.createElement('textarea');
      ta.value = articleUrl; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select(); document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2500);
  };

  return (
    <div className="flex items-center gap-2" data-testid="share-buttons">
      <span className="text-xs text-gray-600 mr-1">Share</span>
      <button
        onClick={shareTelegram}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm text-gray-400 hover:text-[#26A5E4] hover:border-[#26A5E4]/30 transition-all"
        data-testid="share-telegram"
        title="Share on Telegram"
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
        <span className="hidden sm:inline">Share</span>
      </button>
      <button
        onClick={shareLinkedIn}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm text-gray-400 hover:text-[#0A66C2] hover:border-[#0A66C2]/30 transition-all"
        data-testid="share-linkedin"
        title="Share on LinkedIn"
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
        <span className="hidden sm:inline">Share</span>
      </button>
      <a
        href="https://medium.com/@KojirDeFi"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm text-gray-400 hover:text-emerald-400 hover:border-emerald-400/30 transition-all"
        data-testid="share-medium"
        title="Read on Medium"
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zM20.96 12c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z"/></svg>
        <span className="hidden sm:inline">Medium</span>
      </a>
      <button
        onClick={copyLink}
        data-testid="share-copy-link-btn"
        className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm transition-all ${
          linkCopied
            ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
            : 'bg-white/5 border-white/10 text-gray-400 hover:text-white hover:border-white/20'
        }`}
        title="Copy link"
      >
        {linkCopied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">{linkCopied ? 'Copied!' : 'Link'}</span>
      </button>
    </div>
  );
}

const categories = ['All', ...Array.from(new Set(blogPosts.map(p => p.category)))];

export default function Blog() {
  const [activePost, setActivePost] = useState(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      const found = blogPosts.find(p => p.id === hash);
      if (found) return found;
    }
    return null;
  });
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All' ? blogPosts : blogPosts.filter(p => p.category === activeCategory);
  const featuredPost = filtered.find(p => p.featured);
  const regularPosts = filtered.filter(p => !p.featured);

  const openPost = (post) => {
    setActivePost(post);
    window.history.replaceState(null, '', `/blog#${post.id}`);
    window.scrollTo(0, 0);
  };

  const closePost = () => {
    setActivePost(null);
    window.history.replaceState(null, '', '/blog');
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      <AnimatePresence mode="wait">
        {activePost ? (
          <motion.div
            key="article"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Article View */}
            <section className="pt-28 pb-20 px-6">
              <div className="container mx-auto max-w-3xl">
                <button
                  onClick={closePost}
                  className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-8"
                  data-testid="blog-back-btn"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to Blog
                </button>

                <div className={`h-48 md:h-64 rounded-2xl bg-gradient-to-br ${activePost.gradient} mb-8`} />

                <span className="inline-block px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm mb-4">
                  {activePost.category}
                </span>

                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight" data-testid="article-title">
                  {activePost.title}
                </h1>

                <div className="flex items-center justify-between text-sm text-gray-500 mb-10 pb-8 border-b border-white/5">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4" /> {activePost.date}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4" /> {activePost.readTime}
                    </span>
                  </div>
                  <ShareButtons post={activePost} />
                </div>

                <ArticleContent text={activePost.body} />

                {activePost.id === 'agentic-commerce-2030-two-billion-agents' && (
                  <div className="mt-10 p-6 rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.06] to-teal-500/[0.03]" data-testid="blog-manifesto-callout">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <div className="text-[11px] tracking-[3px] text-emerald-400 font-bold uppercase mb-2">Prefer the 45-second version?</div>
                        <div className="text-xl font-black text-white leading-tight">Watch The Recourse Manifesto</div>
                        <div className="text-sm text-slate-400 mt-1">Same argument. Onyx narration. Fits inside a coffee refill.</div>
                      </div>
                      <a
                        href="/manifesto"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black font-bold hover:bg-slate-100 transition-colors"
                        data-testid="blog-manifesto-callout-link"
                      >
                        <span className="w-0 h-0 border-l-[8px] border-l-black border-y-[6px] border-y-transparent" />
                        Play the film
                      </a>
                    </div>
                  </div>
                )}

                <div className="mt-12 pt-8 border-t border-white/5 flex justify-between items-center">
                  <button
                    onClick={closePost}
                    data-testid="blog-close-post-btn"
                    className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1.5 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> All Articles
                  </button>
                  <ShareButtons post={activePost} />
                </div>

                {/* Related Articles */}
                {(() => {
                  const sameCat = blogPosts.filter(p => p.id !== activePost.id && p.category === activePost.category);
                  const others = blogPosts.filter(p => p.id !== activePost.id && p.category !== activePost.category);
                  const related = [...sameCat, ...others].slice(0, 3);
                  if (related.length === 0) return null;
                  return (
                    <div className="mt-16 pt-10 border-t border-white/5" data-testid="related-articles">
                      <h3 className="text-lg font-semibold mb-6 text-gray-300">Continue Reading</h3>
                      <div className="grid sm:grid-cols-3 gap-4">
                        {related.map(post => (
                          <div
                            key={post.id}
                            onClick={() => openPost(post)}
                            className="bg-white/5 rounded-xl overflow-hidden border border-white/5 hover:border-blue-500/30 transition-colors cursor-pointer group"
                            data-testid={`related-${post.id}`}
                          >
                            <div className={`h-24 bg-gradient-to-br ${post.gradient}`} />
                            <div className="p-4">
                              <span className="text-[10px] uppercase tracking-wider text-gray-500">{post.category}</span>
                              <h4 className="text-sm font-medium mt-1 line-clamp-2 group-hover:text-blue-400 transition-colors">{post.title}</h4>
                              <span className="text-xs text-gray-600 mt-2 block">{post.readTime}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </section>
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Blog List */}
            <section className="pt-32 pb-12 px-6">
              <div className="container mx-auto max-w-6xl">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center mb-12"
                >
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                    <span className="bg-gradient-to-r from-orange-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">Blog</span>
                  </h1>
                  <p className="text-lg text-gray-400">
                    Insights, updates, and education from the KOJIRU team
                  </p>

                  {/* Category Filter Bar */}
                  <div className="flex flex-wrap justify-center gap-2 mt-8" data-testid="blog-category-filter">
                    {categories.map(cat => {
                      const count = cat === 'All' ? blogPosts.length : blogPosts.filter(p => p.category === cat).length;
                      return (
                        <button
                          key={cat}
                          onClick={() => setActiveCategory(cat)}
                          data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                            activeCategory === cat
                              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                              : 'bg-white/5 text-gray-500 border border-white/5 hover:text-gray-300 hover:bg-white/10'
                          }`}
                        >
                          {cat} <span className="ml-1 text-xs opacity-60">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>

                {/* Featured Post */}
                {featuredPost && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-12"
                  >
                    <div
                      onClick={() => openPost(featuredPost)}
                      className="bg-white/5 rounded-2xl overflow-hidden border border-white/10 hover:border-blue-500/30 transition-colors group cursor-pointer"
                      data-testid="featured-post"
                    >
                      <div className="grid md:grid-cols-2">
                        <div className={`h-64 md:h-auto bg-gradient-to-br ${featuredPost.gradient}`} />
                        <div className="p-8">
                          <span className="inline-block px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm mb-4">
                            {featuredPost.category}
                          </span>
                          <h2 className="text-2xl font-bold mb-4 group-hover:text-blue-400 transition-colors">
                            {featuredPost.title}
                          </h2>
                          <p className="text-gray-400 mb-6">{featuredPost.excerpt}</p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" /> {featuredPost.date}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" /> {featuredPost.readTime}
                              </span>
                            </div>
                            <span className="text-sm text-blue-400 flex items-center gap-1 group-hover:gap-2 transition-all">
                              Read <ChevronRight className="w-4 h-4" />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </section>

            {/* Posts Grid */}
            <section className="py-12 px-6">
              <div className="container mx-auto max-w-6xl">
                {activeCategory !== 'All' && (
                  <div className="flex items-center justify-between mb-6">
                    <p className="text-sm text-gray-500">
                      Showing <span className="text-white font-medium">{regularPosts.length + (featuredPost ? 1 : 0)}</span> article{regularPosts.length + (featuredPost ? 1 : 0) !== 1 ? 's' : ''} in <span className="text-blue-400">{activeCategory}</span>
                    </p>
                    <button
                      onClick={() => setActiveCategory('All')}
                      className="text-xs text-gray-500 hover:text-white transition-colors"
                      data-testid="clear-filter"
                    >
                      Clear filter
                    </button>
                  </div>
                )}
                {regularPosts.length === 0 && !featuredPost ? (
                  <div className="text-center py-20">
                    <p className="text-gray-500">No articles in this category yet.</p>
                    <button onClick={() => setActiveCategory('All')} className="text-sm text-blue-400 hover:text-blue-300 mt-2" data-testid="blog-empty-view-all-btn">
                      View all articles
                    </button>
                  </div>
                ) : (
                <div className="grid md:grid-cols-3 gap-6" data-testid="blog-grid">
                  {regularPosts.map((post, i) => (
                    <motion.article
                      key={post.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(i * 0.05, 0.3) }}
                      viewport={{ once: true }}
                      onClick={() => openPost(post)}
                      className="bg-white/5 rounded-2xl overflow-hidden border border-white/10 hover:border-blue-500/30 transition-colors group cursor-pointer"
                      data-testid={`blog-post-${post.id}`}
                    >
                      <div className={`h-40 bg-gradient-to-br ${post.gradient}`} />
                      <div className="p-6">
                        <span className="inline-block px-2 py-1 bg-white/10 text-gray-300 rounded text-xs mb-3">
                          {post.category}
                        </span>
                        <h3 className="font-semibold mb-2 group-hover:text-blue-400 transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="text-gray-400 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{post.date}</span>
                          <span className="text-blue-400 flex items-center gap-1">
                            Read <ChevronRight className="w-3 h-3" />
                          </span>
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </div>
                )}
              </div>
            </section>

            {/* Newsletter */}
            <section className="py-20 px-6">
              <div className="container mx-auto max-w-2xl">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl p-12 text-center border border-blue-500/20"
                >
                  <h2 className="text-2xl font-bold mb-4">Subscribe to Our Newsletter</h2>
                  <p className="text-gray-400 mb-6">
                    Get the latest updates, insights, and announcements delivered to your inbox.
                  </p>
                  <div className="flex gap-3 max-w-md mx-auto">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-blue-500"
                      data-testid="newsletter-email"
                    />
                    <button className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl font-medium hover:opacity-90 transition-opacity" data-testid="newsletter-subscribe">
                      Subscribe
                    </button>
                  </div>
                </motion.div>
              </div>
            </section>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
